import { isNull, relations, sql } from 'drizzle-orm';
import {
  bigint,
  boolean,
  check,
  customType,
  doublePrecision,
  index,
  integer,
  jsonb,
  numeric,
  pgEnum,
  pgTable,
  smallint,
  text,
  timestamp,
  uniqueIndex,
  uuid,
  varchar
} from 'drizzle-orm/pg-core';
import type { AdapterAccountType } from 'next-auth/adapters';

// Custom Types
export const bytea = customType<{
  data: Buffer | null;
  notNull: false;
  default: false;
}>({
  dataType() {
    return 'bytea';
  },
  toDriver(val: Buffer | null) {
    return val;
  },
  fromDriver(value: unknown) {
    if (value === null) {
      return null;
    }

    if (value instanceof Buffer) {
      return value;
    }

    if (typeof value === 'string') {
      return Buffer.from(value, 'hex');
    }

    throw new Error(`Unexpected type received from driver: ${typeof value}`);
  }
});

// Enums
export enum FeedbackCategory {
  SUGGESTION = 'suggestion',
  PROBLEM = 'problem',
  QUESTION = 'question'
}

export enum InvitationStatus {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  REVOKED = 'revoked'
}

export enum Role {
  MEMBER = 'member',
  ADMIN = 'admin'
}

export enum WebhookTrigger {
  USER_CREATED = 'userCreated',
  USER_UPDATED = 'userUpdated',
  USER_DELETED = 'userDeleted'
}
// Project Stage - Which step the project is currently in
export enum ProjectStage {
  UPLOAD = 'upload',
  OUTLINE = 'outline',
  SCRIPT = 'script',
  AUDIO_GENERATION = 'audioGeneration',
  JSON_BUILD = 'jsonBuild',
  RENDER = 'render'
}

// Stage Status - The condition of the current stage
export enum StageStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed'
}

// Video Source - How the project's primary video entered the platform.
// EXTENSION:        Chrome extension recording (carries cursorData → auto-zoom eligible).
// INBUILT_RECORDER: In-browser MediaRecorder capture from the new-project flow.
// UPLOAD:           Manual file upload from the user's device.
export enum VideoSource {
  EXTENSION = 'EXTENSION',
  INBUILT_RECORDER = 'INBUILT_RECORDER',
  UPLOAD = 'UPLOAD'
}

export enum AssetType {
  IMAGE = 'image',
  VIDEO = 'video',
  GIF = 'gif',
  AUDIO = 'audio',
  CAPTION = 'caption'
}

export enum JobStatus {
  QUEUED = 'queued',
  PENDING = 'pending',
  IN_PROGRESS = 'inProgress',
  COMPLETED = 'completed',
  FAILED = 'failed'
}

export enum WebhookCallType {
  OUTLINE_GENERATION = 'outlineGeneration',
  SCRIPT_GENERATION = 'scriptGeneration',
  TRANSLATE_GENERATION = 'translateGeneration',
  IMAGE_VERIFICATION = 'imageVerification'
}

export enum RenderStatus {
  QUEUED = 'queued',
  RENDERING = 'rendering',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled'
}

export enum RenderMode {
  LAMBDA = 'lambda',
  BROWSER = 'browser'
}

export enum VersionStatus {
  DRAFT = 'draft',
  CURRENT = 'current',
  ARCHIVED = 'archived'
}

export enum AudioGenerationStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  SUCCESS = 'success',
  FAILED = 'failed'
}

export enum SubscriptionTier {
  FREE = 'free',
  TRIAL = 'trial',
  PLUS = 'plus',
  TEAM = 'team',
  PRO = 'pro',
  // AppSumo lifetime deal. Not a Stripe plan: entitlements come from
  // appsumoLicenses via organizationLimits.appsumoTier (1-5), never from a
  // subscription row.
  APPSUMO = 'appsumo'
}

// AppSumo License Kind - a base license carries the tier (T1-T5); an add-on
// stacks extra units on top of its parent base license.
export enum AppSumoLicenseKind {
  BASE = 'base',
  ADDON = 'addon'
}

// AppSumo License Status.
// INACTIVE:           purchased, not yet redeemed in-app.
// ACTIVE:             redeemed and granting entitlements.
// DEACTIVATED:        refunded/revoked; entitlements withdrawn.
// SUPERSEDED:         replaced by an upgrade/downgrade key.
// SUPERSEDED_PENDING: a deactivate arrived for a bound base license and we are
//                     waiting out the delayed-revoke window in case the paired
//                     upgrade/downgrade is still in flight.
export enum AppSumoLicenseStatus {
  INACTIVE = 'inactive',
  ACTIVE = 'active',
  DEACTIVATED = 'deactivated',
  SUPERSEDED = 'superseded',
  SUPERSEDED_PENDING = 'superseded_pending'
}

// Where an appsumoLicenseEvents row came from: the vendor webhook, the daily
// reconcile cron, or an admin action (which must also set actorUserId).
export enum AppSumoEventSource {
  WEBHOOK = 'webhook',
  RECONCILE = 'reconcile',
  ADMIN = 'admin'
}

// AppSumo Review Claim Status - Lifecycle of a "I reviewed VideoMule on
// AppSumo" bonus-minutes claim. PENDING and APPROVED are the two states the
// per-organization partial unique index keys off, so an org can hold at most
// one open or paid claim; REJECTED releases the index and lets it resubmit.
export enum AppSumoReviewClaimStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED'
}

export enum AssetRole {
  STEP_AUDIO = 'step-audio',
  STEP_CAPTIONS = 'step-captions',
  STEP_VIDEO = 'step-video',
  TAG_AUDIO = 'tag-audio',
  PROJECT_VIDEO = 'project-video',
  PROJECT_THUMBNAIL = 'project-thumbnail',
  PROJECT_RENDER = 'project-render',
  STEP_IMAGE = 'step-image',
  STEP_GIF = 'step-gif',
  BACKGROUND_MUSIC = 'background-music',
  OVERLAY = 'overlay',
  MEDIA_LIBRARY = 'media-library',
  AVATAR_CLIP = 'avatar-clip',
  CUSTOM_AVATAR_SOURCE = 'custom-avatar-source',
  AVATAR_BACKGROUND = 'avatar-background'
}

// Script Version Stage - Pipeline stages for script version processing
export enum ScriptVersionStage {
  SCRIPT = 'script',
  AUDIO_GENERATION = 'audioGeneration',
  AVATAR_GENERATION = 'avatarGeneration',
  JSON_BUILD = 'jsonBuild',
  RENDER = 'render'
}

// Script Edit Type - Types of edits made to scripts
export enum ScriptEditType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete'
}

// Regeneration Reason - Why audio needs to be regenerated
export enum RegenerationReason {
  SCRIPT_CHANGED = 'scriptChanged',
  VOICE_CHANGED = 'voiceChanged',
  SKIP_SILENCE_CHANGED = 'skipSilenceChanged',
  MANUAL = 'manual',
  TEMPLATE_REGENERATION = 'templateRegeneration',
  AVATAR_CHANGED = 'avatarChanged',
  AVATAR_PLACEMENT_CHANGED = 'avatarPlacementChanged'
}

// Template Type - System-provided or custom user templates
export enum TemplateType {
  SYSTEM = 'system',
  CUSTOM = 'custom'
}

// Template Visibility - Who can see/use the template
export enum TemplateVisibility {
  PRIVATE = 'private',
  ORGANIZATION = 'organization',
  PUBLIC = 'public'
}

// Content Analysis - What content to analyze for script generation
// AUTO: N8N decides the effective mode from the transcript verdict
// (audio_only / audio_video / video_only); the outline callback resolves
// projectLanguages.contentAnalysis to the concrete value it picked.
export enum ContentAnalysis {
  BOTH = 'both',
  VIDEO_ONLY = 'video_only',
  AUDIO_ONLY = 'audio_only',
  AUTO = 'auto'
}

// Project Feedback Context - Which part of the project the feedback targets
export enum ProjectFeedbackContext {
  SCRIPT = 'script',
  VIDEO = 'video'
}

// Project Feedback Rating - Sentiment of the feedback
export enum ProjectFeedbackRating {
  POSITIVE = 'positive',
  NEGATIVE = 'negative'
}

// Testimonial Reward Status - Lifecycle of a Senja testimonial we owe credits
// for. GRANTED is what the per-organization unique index keys off, so an org
// can never collect the reward twice. FAILED exists so a grant that threw
// mid-way releases that index and can be retried from the admin queue.
export enum TestimonialRewardStatus {
  PENDING_REVIEW = 'PENDING_REVIEW',
  GRANTED = 'GRANTED',
  SKIPPED = 'SKIPPED',
  FAILED = 'FAILED'
}

// Testimonial Reward Review Reason - Why a testimonial could not be rewarded
// automatically and is waiting for an admin in /admin/testimonials.
export enum TestimonialRewardReviewReason {
  NO_EMAIL = 'NO_EMAIL',
  NO_MATCH = 'NO_MATCH',
  MULTIPLE_ORGS = 'MULTIPLE_ORGS',
  ALREADY_REWARDED = 'ALREADY_REWARDED',
  EXPIRED_PACK = 'EXPIRED_PACK'
}

// StepData types for unified step-level data in scriptVersions
export type StepDataEntry = {
  scripts: Record<string, string>; // { scriptLineIndex: scriptId }
  audioLogId: string | null; // audioGenerationLogs.id or null if needs generation
  tags?: {
    first?: string | null; // resolved tag for first script position ('intro' | 'transition-in' | null = suppressed)
    last?: string | null; // resolved tag for last script position ('outro' | 'transition-out' | null = suppressed)
  };
  // Avatar generation job pointers, paired with `tags`. Only the FIRST and
  // LAST tagged lines of a step ever get an avatar clip, so we key by
  // direction instead of every script line index. Value = avatarGenerationJobs.id;
  // null means "needs regeneration"; absent key means no avatar for that direction.
  avatar?: {
    first?: string | null;
    last?: string | null;
  };
};

export type StepData = Record<string, StepDataEntry>; // { stepId: StepDataEntry }

function enumToPgEnum<T extends Record<string, string>>(myEnum: T) {
  return Object.values(myEnum).map((value) => `${value}`) as [
    T[keyof T],
    ...T[keyof T][]
  ];
}

// use lowercase: https://github.com/drizzle-team/drizzle-orm/issues/1564#issuecomment-2320605690

export const feedbackCategoryEnum = pgEnum(
  'feedbackcategory',
  enumToPgEnum(FeedbackCategory)
);
export const invitationStatusEnum = pgEnum(
  'invitationstatus',
  enumToPgEnum(InvitationStatus)
);
export const roleEnum = pgEnum('Role', enumToPgEnum(Role));
export const webhookTriggerEnum = pgEnum(
  'webhooktrigger',
  enumToPgEnum(WebhookTrigger)
);

// Convert TS enums to pgEnum for Drizzle ORM
export const projectStageEnum = pgEnum(
  'projectStage',
  enumToPgEnum(ProjectStage)
);
export const stageStatusEnum = pgEnum('stageStatus', enumToPgEnum(StageStatus));

export const assetTypeEnum = pgEnum('assetType', enumToPgEnum(AssetType));

export const jobStatusEnum = pgEnum('jobStatus', enumToPgEnum(JobStatus));

export const webhookCallTypeEnum = pgEnum(
  'webhookCallType',
  enumToPgEnum(WebhookCallType)
);

export const renderStatusEnum = pgEnum(
  'renderStatus',
  enumToPgEnum(RenderStatus)
);
export const renderModeEnum = pgEnum('renderMode', enumToPgEnum(RenderMode));
export const versionStatusEnum = pgEnum(
  'versionStatus',
  enumToPgEnum(VersionStatus)
);
export const audioGenerationStatusEnum = pgEnum(
  'audioGenerationStatus',
  enumToPgEnum(AudioGenerationStatus)
);
export const subscriptionTierEnum = pgEnum(
  'subscriptionTier',
  enumToPgEnum(SubscriptionTier)
);
export const assetRoleEnum = pgEnum('assetRole', enumToPgEnum(AssetRole));
export const scriptVersionStageEnum = pgEnum(
  'scriptVersionStage',
  enumToPgEnum(ScriptVersionStage)
);
export const scriptEditTypeEnum = pgEnum(
  'scriptEditType',
  enumToPgEnum(ScriptEditType)
);
export const regenerationReasonEnum = pgEnum(
  'regenerationReason',
  enumToPgEnum(RegenerationReason)
);
export const templateTypeEnum = pgEnum(
  'templateType',
  enumToPgEnum(TemplateType)
);
export const templateVisibilityEnum = pgEnum(
  'templateVisibility',
  enumToPgEnum(TemplateVisibility)
);
export const contentAnalysisEnum = pgEnum(
  'contentAnalysis',
  enumToPgEnum(ContentAnalysis)
);
export const projectFeedbackContextEnum = pgEnum(
  'projectFeedbackContext',
  enumToPgEnum(ProjectFeedbackContext)
);
export const projectFeedbackRatingEnum = pgEnum(
  'projectFeedbackRating',
  enumToPgEnum(ProjectFeedbackRating)
);
export const testimonialRewardStatusEnum = pgEnum(
  'testimonialRewardStatus',
  enumToPgEnum(TestimonialRewardStatus)
);
export const testimonialRewardReviewReasonEnum = pgEnum(
  'testimonialRewardReviewReason',
  enumToPgEnum(TestimonialRewardReviewReason)
);

// Custom Avatar Status - State of a user-trained custom avatar.
// PENDING = training on HeyGen; PENDING_CONSENT = trained but the identity
// consent recording (digital twins only) hasn't been approved yet; READY =
// selectable for generation; FAILED = dead end (frees its slot).
export const customAvatarStatusEnum = pgEnum('customAvatarStatus', [
  'PENDING',
  'PENDING_CONSENT',
  'READY',
  'FAILED'
]);

// Avatar Generation Job Status - State of a HeyGen render job
export const avatarGenerationJobStatusEnum = pgEnum(
  'avatarGenerationJobStatus',
  ['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED']
);

// Avatar Quality Tier - user-facing quality selection (HeyGen v3).
// The tier only selects the resolved engine (NORMAL -> avatar_iii,
// STANDARD -> avatar_iv (default), HIGH -> avatar_v); the actual burn
// multiplier is derived from HeyGen's avatar type x engine USD rate grid
// (HEYGEN_COST_PER_MIN_USD in @workspace/billing/avatar-quality), not from
// the tier alone. Tier availability is filtered per look by its
// supportedApiEngines; see resolveAvatarEngine.
export const avatarQualityTierEnum = pgEnum('avatarQualityTier', [
  'NORMAL',
  'STANDARD',
  'HIGH'
]);

// HeyGen Engine - the actual HeyGen v3 rendering engine used for a clip.
// Distinct from avatarQualityTierEnum: the quality tier is the user-facing
// selection, this is the resolved engine (a tier the look can't render
// falls back to a supported engine and bills at that engine's rate).
export const heygenEngineEnum = pgEnum('heygenEngine', [
  'avatar_iii',
  'avatar_iv',
  'avatar_v'
]);

export const favoriteVoiceScopeEnum = pgEnum('favoriteVoiceScope', [
  'personal',
  'organization'
]);

export const favoriteAvatarScopeEnum = pgEnum('favoriteAvatarScope', [
  'personal',
  'organization'
]);

// What a favoriteAvatars row points at. Neither target is a stable FK:
// 'stock_character' is a derived grouping key over avatarOptions looks, and
// 'custom_identity' is a HeyGen avatar-group id (or the customAvatars row id
// for a look with no group yet).
export const favoriteAvatarTargetEnum = pgEnum('favoriteAvatarTarget', [
  'stock_character',
  'custom_identity'
]);

// BYOK Key Status - State of a customer-supplied ElevenLabs API key.
// ACTIVE:  key has been validated and is currently operational.
// INVALID: a recent API call with this key failed (e.g. 401); needs re-entry.
// REVOKED: customer explicitly removed the integration.
export const byokKeyStatusEnum = pgEnum('byokKeyStatus', [
  'ACTIVE',
  'INVALID',
  'REVOKED'
]);

// Custom Voice Status - State of a BYOK-cached ElevenLabs voice.
// READY:   voice fetched from customer's EL account and usable for TTS immediately.
// PENDING: reserved for forward-compat with a possible future in-app clone flow.
// FAILED:  sync or validation error; statusMessage holds details.
export const customVoiceStatusEnum = pgEnum('customVoiceStatus', [
  'READY',
  'PENDING',
  'FAILED'
]);

export const videoSourceEnum = pgEnum('videoSource', enumToPgEnum(VideoSource));
export const appsumoLicenseKindEnum = pgEnum(
  'appsumoLicenseKind',
  enumToPgEnum(AppSumoLicenseKind)
);
export const appsumoLicenseStatusEnum = pgEnum(
  'appsumoLicenseStatus',
  enumToPgEnum(AppSumoLicenseStatus)
);
export const appsumoEventSourceEnum = pgEnum(
  'appsumoEventSource',
  enumToPgEnum(AppSumoEventSource)
);
export const appsumoReviewClaimStatusEnum = pgEnum(
  'appsumoReviewClaimStatus',
  enumToPgEnum(AppSumoReviewClaimStatus)
);

// Tables

export const apiKeyTable = pgTable(
  'apiKey',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    description: varchar('description', { length: 70 }).notNull(),
    hashedKey: text('hashedKey').notNull(),
    expiresAt: timestamp('expiresAt', { precision: 3, mode: 'date' }),
    lastUsedAt: timestamp('lastUsedAt', { precision: 3, mode: 'date' }),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    uniqueIndex('IX_apiKey_hashedKey_unique').using(
      'btree',
      table.hashedKey.asc().nullsLast().op('text_ops')
    ),
    index('IX_apiKey_organizationId').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const authenticatorAppTable = pgTable(
  'authenticatorApp',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    userId: uuid('userId')
      .notNull()
      .references(() => userTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    accountName: varchar('accountName', { length: 255 }).notNull(),
    issuer: varchar('issuer', { length: 255 }).notNull(),
    secret: varchar('secret', { length: 255 }).notNull(),
    recoveryCodes: varchar('recoveryCodes', { length: 1024 }).notNull(),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    uniqueIndex('IX_authenticatorApp_userId_unique').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const changeEmailRequestTable = pgTable(
  'changeEmailRequest',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    userId: uuid('userId')
      .notNull()
      .references(() => userTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    email: text('email').notNull(),
    expires: timestamp('expires', { precision: 3, mode: 'date' }).notNull(),
    valid: boolean('valid').default(false).notNull(),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index('IX_changeEmailRequest_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const invitationTable = pgTable(
  'invitation',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    token: uuid('token').notNull().defaultRandom(),
    email: varchar('email', { length: 255 }).notNull(),
    role: roleEnum('role').default(Role.MEMBER).notNull(),
    status: invitationStatusEnum('status')
      .default(InvitationStatus.PENDING)
      .notNull(),
    lastSentAt: timestamp('lastSentAt', { precision: 3, mode: 'date' }),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index('IX_invitation_organizationId').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops')
    ),
    index('IX_invitation_token').using(
      'btree',
      table.token.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const accountTable = pgTable(
  'account',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    userId: uuid('userId')
      .notNull()
      .references(() => userTable.id, { onDelete: 'cascade' }),
    type: text('type').$type<AdapterAccountType>().notNull(),
    provider: text('provider').notNull(),
    providerAccountId: text('providerAccountId').notNull(),
    refresh_token: text('refresh_token'),
    access_token: text('access_token'),
    expires_at: integer('expires_at'),
    token_type: text('token_type'),
    scope: text('scope'),
    id_token: text('id_token'),
    session_state: text('session_state'),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    uniqueIndex('IX_account_provider_providerAccountId_unique').using(
      'btree',
      table.provider.asc().nullsLast().op('text_ops'),
      table.providerAccountId.asc().nullsLast().op('text_ops')
    ),
    index('IX_account_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const feedbackTable = pgTable(
  'feedback',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    userId: uuid('userId').references(() => userTable.id, {
      onDelete: 'set null',
      onUpdate: 'cascade'
    }),
    category: feedbackCategoryEnum('category')
      .default(FeedbackCategory.SUGGESTION)
      .notNull(),
    message: varchar('message', { length: 4000 }).notNull(),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index('IX_feedback_organizationId').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops')
    ),
    index('IX_feedback_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const notificationTable = pgTable(
  'notification',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    userId: uuid('userId')
      .notNull()
      .references(() => userTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    subject: varchar('subject', { length: 128 }),
    content: varchar('content', { length: 8000 }).notNull(),
    link: varchar('link', { length: 2000 }),
    seenAt: timestamp('seenAt', { precision: 3, mode: 'date' }),
    dismissed: boolean('dismissed').default(false).notNull(),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index('IX_notification_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const resetPasswordRequestTable = pgTable(
  'resetPasswordRequest',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    email: text('email').notNull(),
    expires: timestamp('expires', { precision: 3, mode: 'date' }).notNull(),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index('IX_resetPasswordRequest_email').using(
      'btree',
      table.email.asc().nullsLast().op('text_ops')
    )
  ]
);

export const userImageTable = pgTable(
  'userImage',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    userId: uuid('userId')
      .notNull()
      .references(() => userTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    data: bytea('data'),
    contentType: varchar('contentType', { length: 255 }),
    hash: varchar('hash', { length: 64 })
  },
  (table) => [
    index('IX_userImage_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const userTable = pgTable(
  'user',
  {
    id: uuid('id')
      .primaryKey()
      .$defaultFn(() => crypto.randomUUID()),
    email: text('email').unique(),
    emailVerified: timestamp('emailVerified', { precision: 3, mode: 'date' }),
    password: varchar('password', { length: 60 }),
    lastLogin: timestamp('lastLogin', { precision: 3, mode: 'date' }),
    // One-trial-per-user (ever): set the first time any org this user OWNS syncs
    // to a paid/trialing tier (see syncOrganizationLimitsToTier). Never cleared —
    // survives org soft-deletion (which hard-deletes membership rows) and
    // ownership transfer. Paying without trialing also sets it: paid-before users
    // get no trial on new orgs.
    trialUsedAt: timestamp('trialUsedAt', { precision: 3, mode: 'date' }),
    locale: varchar('locale', { length: 8 }).default('en-US').notNull(),
    completedOnboarding: boolean('completedOnboarding')
      .default(false)
      .notNull(),
    enabledContactsNotifications: boolean('enabledContactsNotifications')
      .default(false)
      .notNull(),
    enabledInboxNotifications: boolean('enabledInboxNotifications')
      .default(false)
      .notNull(),
    enabledNewsletter: boolean('enabledNewsletter').default(false).notNull(),
    enabledProductUpdates: boolean('enabledProductUpdates')
      .default(false)
      .notNull(),
    enabledWeeklySummary: boolean('enabledWeeklySummary')
      .default(false)
      .notNull(),
    image: varchar('image', { length: 2048 }),
    name: varchar('name', { length: 64 }).notNull(),
    phone: varchar('phone', { length: 32 }),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date()),
    deletedAt: timestamp('deletedAt', { precision: 3, mode: 'date' })
  },
  (table) => [
    uniqueIndex('IX_user_email_unique')
      .on(table.email)
      .where(isNull(table.deletedAt)),
    index('IX_user_name').using(
      'btree',
      table.name.asc().nullsLast().op('text_ops')
    ),
    index('IX_user_createdAt').using('btree', table.createdAt.asc())
  ]
);

export const sessionTable = pgTable(
  'session',
  {
    sessionToken: text('sessionToken').primaryKey(),
    userId: uuid('userId')
      .notNull()
      .references(() => userTable.id, { onDelete: 'cascade' }),
    expires: timestamp('expires', { mode: 'date' }).notNull(),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index('IX_session_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    ),
    index('IX_session_createdAt').using('btree', table.createdAt.asc())
  ]
);

export const verificationTokenTable = pgTable(
  'verificationToken',
  {
    identifier: text('identifier').notNull(),
    token: text('token').notNull(),
    expires: timestamp('expires', { precision: 3, mode: 'date' }).notNull()
  },
  (table) => [
    uniqueIndex('IX_verificationToken_identifier_unique').using(
      'btree',
      table.identifier.asc().nullsLast().op('text_ops'),
      table.token.asc().nullsLast().op('text_ops')
    )
  ]
);

export const webhookTable = pgTable(
  'webhook',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    secret: varchar('secret', { length: 1024 }),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp('updatedAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull(),
    triggers: webhookTriggerEnum('triggers').array().notNull(),
    url: varchar('url', { length: 2000 }).notNull()
  },
  (table) => [
    {
      organizationIdIndex: index('IX_webhook_organizationId').on(
        table.organizationId
      )
    }
  ]
);

export const membershipTable = pgTable(
  'membership',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    userId: uuid('userId')
      .notNull()
      .references(() => userTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    role: roleEnum('role').default(Role.MEMBER).notNull(),
    isOwner: boolean('isOwner').default(false).notNull(),
    createdAt: timestamp('createdAt', { precision: 3, mode: 'date' })
      .defaultNow()
      .notNull()
  },
  (table) => [
    uniqueIndex('IX_membership_organizationId_userId_unique').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops'),
      table.userId.asc().nullsLast().op('uuid_ops')
    ),
    index('IX_membership_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    ),
    index('IX_membership_organizationId').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const organizationLogoTable = pgTable(
  'organizationLogo',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    data: bytea('data'),
    contentType: varchar('contentType', { length: 255 }),
    hash: varchar('hash', { length: 64 })
  },
  (table) => [
    index('IX_organizationLogo_organizationId').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const organizationTable = pgTable(
  'organization',
  {
    id: uuid('id').primaryKey().notNull().defaultRandom(),
    name: varchar('name', { length: 255 }).notNull(),
    address: varchar('address', { length: 255 }),
    email: varchar('email', { length: 255 }),
    website: varchar('website', { length: 2000 }),
    phone: varchar('phone', { length: 32 }),
    facebookPage: varchar('facebookPage', { length: 2000 }),
    instagramProfile: varchar('instagramProfile', { length: 2000 }),
    linkedInProfile: varchar('linkedInProfile', { length: 2000 }),
    tikTokProfile: varchar('tikTokProfile', { length: 2000 }),
    xProfile: varchar('xProfile', { length: 2000 }),
    youTubeChannel: varchar('youTubeChannel', { length: 2000 }),
    logo: varchar('logo', { length: 2048 }),
    slug: varchar('slug', { length: 255 }).notNull(),
    billingCustomerId: varchar('billingCustomerId', { length: 255 }),
    billingEmail: varchar('billingEmail', { length: 255 }),
    calliyoLeadId: varchar('calliyoLeadId', { length: 255 }),
    billingLine1: varchar('billingLine1', { length: 255 }),
    billingLine2: varchar('billingLine2', { length: 255 }),
    billingCountry: varchar('billingCountry', { length: 3 }),
    billingPostalCode: varchar('billingPostalCode', { length: 16 }),
    billingCity: varchar('billingCity', { length: 255 }),
    billingState: varchar('billingState', { length: 255 }),
    deletedAt: timestamp('deletedAt', { withTimezone: true })
  },
  (table) => [
    index('IX_organization_billingCustomerId').using(
      'btree',
      table.billingCustomerId.asc().nullsLast().op('text_ops')
    ),
    uniqueIndex('IX_organization_slug_unique')
      .on(table.slug)
      .where(isNull(table.deletedAt))
  ]
);

export const subscriptionTable = pgTable(
  'subscription',
  {
    id: text('id').primaryKey(),
    organizationId: uuid('organizationId').notNull(),
    status: varchar('status', { length: 64 }).notNull(),
    active: boolean('active').notNull().default(false),
    provider: varchar('provider', { length: 32 }).notNull(),
    cancelAtPeriodEnd: boolean('cancelAtPeriodEnd').notNull().default(false),
    currency: varchar('currency', { length: 3 }).notNull(),
    periodStartsAt: timestamp('periodStartsAt', {
      withTimezone: true,
      precision: 6
    }).notNull(),
    periodEndsAt: timestamp('periodEndsAt', {
      withTimezone: true,
      precision: 6
    }).notNull(),
    trialStartsAt: timestamp('trialStartsAt', {
      withTimezone: true,
      precision: 6
    }),
    trialEndsAt: timestamp('trialEndsAt', { withTimezone: true, precision: 6 }),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
  },
  (table) => [
    index('IX_subscription_organizationId').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const subscriptionItemTable = pgTable(
  'subscriptionItem',
  {
    id: text('id').primaryKey(),
    subscriptionId: text('subscriptionId').notNull(),
    quantity: integer('quantity').notNull(),
    productId: text('productId').notNull(),
    variantId: text('variantId').notNull(),
    priceAmount: doublePrecision('priceAmount'),
    interval: text('interval').notNull(),
    intervalCount: integer('intervalCount').notNull(),
    type: text('type'),
    model: text('model')
  },
  (table) => [
    index('IX_subscriptionItem_subscriptionId').using(
      'btree',
      table.subscriptionId.asc().nullsLast()
    )
  ]
);

export const orderTable = pgTable(
  'order',
  {
    id: text('id').primaryKey(),
    organizationId: uuid('organizationId').notNull(),
    status: varchar('status', { length: 64 }).notNull(),
    provider: varchar('provider', { length: 32 }).notNull(),
    totalAmount: doublePrecision('totalAmount').notNull(),
    currency: varchar('currency', { length: 3 }).notNull(),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
  },
  (table) => [
    index('IX_order_organizationId').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

export const orderItemTable = pgTable(
  'orderItem',
  {
    id: text('id').primaryKey(),
    orderId: text('orderId').notNull(),
    quantity: integer('quantity').notNull(),
    productId: text('productId').notNull(),
    variantId: text('variantId').notNull(),
    priceAmount: doublePrecision('priceAmount'),
    type: text('type'),
    model: text('model')
  },
  (table) => [
    index('IX_orderItem_orderId').using(
      'btree',
      table.orderId.asc().nullsLast()
    )
  ]
);

// Language Options Table
export const languageOptions = pgTable(
  'languageOptions',
  {
    id: uuid('id').primaryKey().defaultRandom(),

    // ElevenLabs language code (e.g., "en", "es", "fr")
    languageCode: varchar('languageCode', { length: 10 }).notNull(),
    languageName: varchar('languageName', { length: 100 }).notNull(),
    flagUrl: varchar('flagUrl', { length: 500 }),

    // Model support flags (populated by sync-languages-from-models action)
    supportsV2: boolean('supportsV2').notNull().default(false),
    supportsV3: boolean('supportsV3').notNull().default(false),

    // Pre-computed voice count (updated during voice/language sync)
    voiceCount: integer('voiceCount').notNull().default(0),

    isActive: boolean('isActive').notNull().default(true),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
    deletedAt: timestamp('deletedAt', { withTimezone: true })
  },
  (table) => [
    uniqueIndex('IX_languageOptions_languageCode_unique').using(
      'btree',
      table.languageCode.asc()
    )
  ]
);

// Voice Options Table - Stores all ElevenLabs voice data
export const voiceOptions = pgTable(
  'voiceOptions',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    displayOrder: integer('displayOrder'),
    // Provider voice ID (THE critical field for API calls).
    // For voiceProviderId='elevenlabs' this is the ElevenLabs voice ID.
    // For voiceProviderId='google' this holds the Google Chirp voice name
    // (e.g. "Chirp3-HD-Kore") instead — this column is NOT NULL and uniquely
    // indexed, so Google rows share it rather than adding a second column.
    elevenLabsVoiceId: varchar('elevenLabsVoiceId', { length: 50 }).notNull(),

    // Voice basic info
    voiceName: text('voiceName').notNull(),
    description: text('description'),
    category: varchar('category', { length: 50 }), // "professional", etc.

    // Primary language relationship (nullable after voice deduplication migration)
    languageId: uuid('languageId').references(() => languageOptions.id, {
      onDelete: 'cascade',
      onUpdate: 'cascade'
    }),

    // Voice characteristics from labels
    gender: varchar('gender', { length: 20 }), // "male", "female"
    age: varchar('age', { length: 20 }), // "young", "middle_aged", "old"
    accent: varchar('accent', { length: 50 }), // "american", "british", "indian"
    useCase: varchar('useCase', { length: 50 }), // "conversational", "narrative_story"
    descriptive: varchar('descriptive', { length: 50 }), // "casual", "professional"
    locale: varchar('locale', { length: 10 }), // "en-US", "en-GB"

    // Preview and media
    previewUrl: text('previewUrl'),

    // Multi-language support (store all verified_languages as JSONB)
    verifiedLanguages: jsonb('verifiedLanguages'), // Store array of {language, model_id, accent, locale, preview_url}

    // Model support
    highQualityModelIds: jsonb('highQualityModelIds'), // Array of model IDs

    // Metadata
    isOwner: boolean('isOwner').default(false),
    voiceProviderId: text('voiceProviderId').default('elevenlabs'),

    // Sync tracking
    lastSyncedAt: timestamp('lastSyncedAt', { withTimezone: true })
      .notNull()
      .defaultNow(),

    isActive: boolean('isActive').notNull().default(true),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
    deletedAt: timestamp('deletedAt', { withTimezone: true })
  },
  (table) => [
    // Unique constraint: one voice record per ElevenLabs voice (deduplicated)
    uniqueIndex('IX_voiceOptions_elevenLabsVoiceId_unique')
      .on(table.elevenLabsVoiceId)
      .where(isNull(table.deletedAt)),
    index('IX_voiceOptions_gender').using('btree', table.gender.asc()),
    index('IX_voiceOptions_accent').using('btree', table.accent.asc()),
    index('IX_voiceOptions_useCase').using('btree', table.useCase.asc()),
    index('IX_voiceOptions_isActive').using('btree', table.isActive.asc())
  ]
);

// Shared Voices - ElevenLabs community/shared voices (isOwner=false) that support eleven_v3
// Populated by a dedicated "Sync Shared Voices" admin action (separate from voiceOptions sync).
// Stores our own generated TTS preview audio (S3 URL) instead of ElevenLabs' previewUrl.
export const sharedVoices = pgTable(
  'sharedVoices',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    elevenLabsVoiceId: varchar('elevenLabsVoiceId', { length: 50 }).notNull(),
    voiceName: text('voiceName').notNull(),
    description: text('description'),
    category: varchar('category', { length: 50 }),
    gender: varchar('gender', { length: 20 }),
    age: varchar('age', { length: 20 }),
    accent: varchar('accent', { length: 50 }),
    useCase: varchar('useCase', { length: 50 }),
    descriptive: varchar('descriptive', { length: 50 }),
    locale: varchar('locale', { length: 10 }),
    verifiedLanguages: jsonb('verifiedLanguages').default([]),
    highQualityModelIds: jsonb('highQualityModelIds').default([]),
    previewUrl: text('previewUrl'), // S3 URL of our generated TTS preview
    previewGeneratedAt: timestamp('previewGeneratedAt', { withTimezone: true }),
    isActive: boolean('isActive').notNull().default(true),
    lastSyncedAt: timestamp('lastSyncedAt', { withTimezone: true }),
    deletedAt: timestamp('deletedAt', { withTimezone: true }),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date())
  },
  (table) => [
    uniqueIndex('IX_sharedVoices_elevenLabsVoiceId_unique')
      .on(table.elevenLabsVoiceId)
      .where(isNull(table.deletedAt)),
    index('IX_sharedVoices_gender').using('btree', table.gender.asc()),
    index('IX_sharedVoices_accent').using('btree', table.accent.asc()),
    index('IX_sharedVoices_isActive').using('btree', table.isActive.asc())
  ]
);

// V3 Language Voices - Admin-curated voice assignments for V3-only languages
// (languages where supportsV3=true, supportsV2=false, and no verified voices exist)
export const v3LanguageVoices = pgTable(
  'v3LanguageVoices',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    languageId: uuid('languageId')
      .notNull()
      .references(() => languageOptions.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    voiceId: uuid('voiceId')
      .notNull()
      .references(() => sharedVoices.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    addedByUserId: uuid('addedByUserId').references(() => userTable.id, {
      onDelete: 'set null',
      onUpdate: 'cascade'
    }),
    displayOrder: integer('displayOrder').notNull().default(0),
    deletedAt: timestamp('deletedAt', { withTimezone: true }),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date())
  },
  (table) => [
    uniqueIndex('IX_v3LanguageVoices_languageId_voiceId_unique')
      .on(table.languageId, table.voiceId)
      .where(isNull(table.deletedAt)),
    index('IX_v3LanguageVoices_languageId').using(
      'btree',
      table.languageId.asc()
    ),
    index('IX_v3LanguageVoices_voiceId').using('btree', table.voiceId.asc()),
    index('IX_v3LanguageVoices_addedByUserId').using(
      'btree',
      table.addedByUserId.asc()
    )
  ]
);

// Favorite Voices - Personal and organization-scoped voice favorites
export const favoriteVoices = pgTable(
  'favoriteVoices',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: uuid('userId').references(() => userTable.id, {
      onDelete: 'cascade',
      onUpdate: 'cascade'
    }),
    organizationId: uuid('organizationId').references(
      () => organizationTable.id,
      {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }
    ),
    voiceId: uuid('voiceId').references(() => voiceOptions.id, {
      onDelete: 'cascade',
      onUpdate: 'cascade'
    }),
    sharedVoiceId: uuid('sharedVoiceId').references(() => sharedVoices.id, {
      onDelete: 'cascade',
      onUpdate: 'cascade'
    }),
    customVoiceId: uuid('customVoiceId').references(() => customVoices.id, {
      onDelete: 'cascade'
    }),
    scope: favoriteVoiceScopeEnum('scope').notNull(),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow()
  },
  (table) => [
    uniqueIndex('IX_favoriteVoices_userId_voiceId_unique').on(
      table.userId,
      table.voiceId
    ),
    uniqueIndex('IX_favoriteVoices_userId_sharedVoiceId_unique').on(
      table.userId,
      table.sharedVoiceId
    ),
    uniqueIndex('IX_favoriteVoices_organizationId_voiceId_unique').on(
      table.organizationId,
      table.voiceId
    ),
    uniqueIndex('IX_favoriteVoices_organizationId_sharedVoiceId_unique').on(
      table.organizationId,
      table.sharedVoiceId
    ),
    uniqueIndex('IX_favoriteVoices_userId_customVoiceId_unique').on(
      table.userId,
      table.customVoiceId
    ),
    uniqueIndex('IX_favoriteVoices_organizationId_customVoiceId_unique').on(
      table.organizationId,
      table.customVoiceId
    ),
    index('IX_favoriteVoices_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    ),
    index('IX_favoriteVoices_organizationId').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

/**
 * Favorited avatars — the avatar counterpart of `favoriteVoices`, with the
 * same nullable-owner + scope model ('personal' rows carry userId, team rows
 * carry organizationId) and the same hard-delete-on-untoggle behaviour (no
 * deletedAt).
 *
 * Unlike voices, the target is stored as `targetType` + `targetKey` rather
 * than FK columns, because neither favoritable thing is an FK-able row:
 * a stock "character" is a grouping key derived from avatarOptions display
 * names, and a custom identity is a HeyGen avatar-group id spanning several
 * customAvatars looks. Consequence: no cascade — deleting a custom identity
 * must clear its favorites explicitly, and stock keys can orphan if HeyGen
 * renames a character (orphans simply stop resolving and render as nothing).
 */
export const favoriteAvatars = pgTable(
  'favoriteAvatars',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: uuid('userId').references(() => userTable.id, {
      onDelete: 'cascade',
      onUpdate: 'cascade'
    }),
    organizationId: uuid('organizationId').references(
      () => organizationTable.id,
      {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }
    ),
    targetType: favoriteAvatarTargetEnum('targetType').notNull(),
    /** Stock character grouping key, or a custom identity's group id. */
    targetKey: varchar('targetKey', { length: 200 }).notNull(),
    scope: favoriteAvatarScopeEnum('scope').notNull(),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow()
  },
  (table) => [
    uniqueIndex('IX_favoriteAvatars_userId_target_unique').on(
      table.userId,
      table.targetType,
      table.targetKey
    ),
    uniqueIndex('IX_favoriteAvatars_organizationId_target_unique').on(
      table.organizationId,
      table.targetType,
      table.targetKey
    ),
    index('IX_favoriteAvatars_userId').using(
      'btree',
      table.userId.asc().nullsLast().op('uuid_ops')
    ),
    index('IX_favoriteAvatars_organizationId').using(
      'btree',
      table.organizationId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

// Language Voice Order - Per-language display ordering for voiceOptions voices
export const languageVoiceOrder = pgTable(
  'languageVoiceOrder',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    languageId: uuid('languageId')
      .notNull()
      .references(() => languageOptions.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    voiceOptionId: uuid('voiceOptionId')
      .notNull()
      .references(() => voiceOptions.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade'
      }),
    displayOrder: integer('displayOrder').notNull().default(0),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date())
  },
  (table) => [
    uniqueIndex('IX_languageVoiceOrder_languageId_voiceOptionId_unique').using(
      'btree',
      table.languageId.asc().nullsLast().op('uuid_ops'),
      table.voiceOptionId.asc().nullsLast().op('uuid_ops')
    ),
    index('IX_languageVoiceOrder_languageId').using(
      'btree',
      table.languageId.asc().nullsLast().op('uuid_ops')
    )
  ]
);

// Avatar Character Order - Admin-controlled display ordering for STOCK avatar
// characters. Keyed by the DERIVED character key (lib/avatars/
// group-avatar-characters.ts stockCharacterKey), not an avatarOptions FK:
// the user orders characters (groups of looks), and a string key survives
// catalog syncs — new looks of an ordered character inherit its position.
// Same precedent as favoriteAvatars.targetKey. Sparse override rows:
// characters without a row sink below ordered ones (?? Infinity at read
// time, mirroring languageVoiceOrder). No deletedAt — hard delete, like
// languageVoiceOrder.
export const avatarCharacterOrder = pgTable(
  'avatarCharacterOrder',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    characterKey: varchar('characterKey', { length: 200 }).notNull(),
    displayOrder: integer('displayOrder').notNull().default(0),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date())
  },
  (table) => [
    uniqueIndex('IX_avatarCharacterOrder_characterKey_unique').using(
      'btree',
      table.characterKey.asc().nullsLast().op('text_ops')
    )
  ]
);

// Avatar Character Meta - Admin-set per-character metadata for STOCK avatar
// characters, currently the recommended voice shown in the picker (selecting
// the avatar auto-selects/offers this voice). Keyed by the same derived
// character key as avatarCharacterOrder, but a SEPARATE table: the order
// table's save path is delete-all-then-insert and holds only featured rows,
// while recommendations are per-character upserts on any character. Exactly
// one of the two voice FKs may be set (global stock catalog voices only —
// customVoices are org-scoped and never recommendable). No deletedAt — hard
// delete, like avatarCharacterOrder.
export const avatarCharacterMeta = pgTable(
  'avatarCharacterMeta',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    characterKey: varchar('characterKey', { length: 200 }).notNull(),
    recommendedVoiceId: uuid('recommendedVoiceId').references(
      () => voiceOptions.id,
      { onDelete: 'set null' }
    ),
    recommendedSharedVoiceId: uuid('recommendedSharedVoiceId').references(
      () => sharedVoices.id,
      { onDelete: 'set null' }
    ),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date())
  },
  (table) => [
    uniqueIndex('IX_avatarCharacterMeta_characterKey_unique').using(
      'btree',
      table.characterKey.asc().nullsLast().op('text_ops')
    ),
    check(
      'CK_avatarCharacterMeta_one_voice',
      sql`num_nonnulls("recommendedVoiceId", "recommendedSharedVoiceId") <= 1`
    )
  ]
);

// Projects
export const projects = pgTable(
  'projects',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: uuid('userId').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, { onDelete: 'restrict' }),
    projectAssetId: uuid('projectAssetId'), // The input video's project_asset_id
    thumbnailS3Key: text('thumbnailS3Key'), // S3 key for video thumbnail
    thumbnailS3Url: text('thumbnailS3Url'), // S3 URL for video thumbnail
    videoTitle: text('videoTitle').notNull(),
    videoType: text('videoType'), // Set from image verification response (e.g., "tutorial", "demo")
    videoGoal: text('videoGoal'), // Set from outline generation response
    context: text('context'), // User-supplied reference context forwarded to script generation only
    // How the primary video entered the platform. Auto-zoom is only meaningful
    // for EXTENSION-sourced projects (cursor clicks). Others hide the toggle.
    videoSource: videoSourceEnum('videoSource')
      .notNull()
      .default(VideoSource.UPLOAD),
    // Default language/voice for new projectLanguages
    defaultLanguageId: uuid('defaultLanguageId')
      .notNull()
      .references(() => languageOptions.id),
    defaultVoiceId: uuid('defaultVoiceId').references(() => voiceOptions.id),
    // Upload stage tracking (project-level, shared across all languages)
    currentStage: projectStageEnum('currentStage')
      .notNull()
      .default(ProjectStage.UPLOAD),
    stageStatus: stageStatusEnum('stageStatus')
      .notNull()
      .default(StageStatus.PENDING),
    stageErrorMessage: text('stageErrorMessage'),
    // User attribution
    createdBy: uuid('createdBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    editedBy: uuid('editedBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    deletedBy: uuid('deletedBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
    deletedAt: timestamp('deletedAt', { withTimezone: true }),
    isActive: boolean('isActive').default(true),
    isSystem: boolean('isSystem').notNull().default(false),
    cursorData: jsonb('cursorData'), // Cursor metadata keyed by editor assetId: { [assetId]: CursorMetadata }
    // Video trim times (seconds) - null means use full video
    trimStartTime: integer('trimStartTime'), // Start time in seconds, null = 0
    trimEndTime: integer('trimEndTime'), // End time in seconds, null = full duration
    showCursorHighlight: boolean('showCursorHighlight').notNull().default(true) // Show yellow cursor halo in editor/export; toggled from extension + editor
  },
  (table) => [
    index('IX_projects_userId').using('btree', table.userId.asc()),
    index('IX_projects_organizationId').using(
      'btree',
      table.organizationId.asc()
    ),
    index('IX_projects_currentStage').using('btree', table.currentStage.asc()),
    index('IX_projects_stageStatus').using('btree', table.stageStatus.asc()),
    index('idx_projects_is_system').using('btree', table.isSystem.asc()),
    index('IX_projects_createdAt').using('btree', table.createdAt.asc())
  ]
);

// Project Languages - Pipeline execution unit for each language
export const projectLanguages = pgTable(
  'projectLanguages',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    projectId: uuid('projectId')
      .notNull()
      .references(() => projects.id, { onDelete: 'cascade' }),
    languageId: uuid('languageId')
      .notNull()
      .references(() => languageOptions.id, { onDelete: 'cascade' }),
    voiceId: uuid('voiceId').references(() => voiceOptions.id, {
      onDelete: 'cascade'
    }),
    sharedVoiceId: uuid('sharedVoiceId').references(() => sharedVoices.id, {
      onDelete: 'cascade'
    }),
    customVoiceId: uuid('customVoiceId').references(() => customVoices.id, {
      onDelete: 'set null'
    }),

    // TTS model selection (e.g. 'eleven_v3', 'eleven_multilingual_v2')
    selectedModelId: varchar('selectedModelId', { length: 50 }).default(
      'eleven_multilingual_v2'
    ),

    // AI avatar selection (mirrors voice selection — applied at project creation)
    avatarId: uuid('avatarId').references(() => avatarOptions.id, {
      onDelete: 'set null'
    }),
    customAvatarId: uuid('customAvatarId').references(() => customAvatars.id, {
      onDelete: 'set null'
    }),
    avatarUseIntro: boolean('avatarUseIntro').notNull().default(false),
    avatarUseOutro: boolean('avatarUseOutro').notNull().default(false),
    avatarUseTransitions: boolean('avatarUseTransitions')
      .notNull()
      .default(false),
    // HeyGen v3 quality tier — per-language default that new scriptVersions
    // inherit from (mirrors autoZoom inheritance pattern).
    // NOTE: the DB default deliberately stays 'STANDARD' while the app-level
    // DEFAULT_AVATAR_QUALITY_TIER is 'HIGH' — every INSERT site sets this
    // column explicitly (Version Field Inheritance rule), so the column
    // default is never exercised. Do NOT trust it from a new insert site.
    avatarQuality: avatarQualityTierEnum('avatarQuality')
      .notNull()
      .default('STANDARD'),

    // Pipeline state (moved from projects)
    currentStage: projectStageEnum('currentStage')
      .notNull()
      .default(ProjectStage.OUTLINE),
    stageStatus: stageStatusEnum('stageStatus')
      .notNull()
      .default(StageStatus.PENDING),
    stageErrorMessage: text('stageErrorMessage'),

    // NOTE: currentScriptVersionId intentionally has NO FK constraint due to circular reference
    // with scriptVersions table. The relationship is enforced via Drizzle relations API
    // and application-level validation. If scriptVersion is deleted, this field should be
    // set to null manually in application code.
    currentScriptVersionId: uuid('currentScriptVersionId'),

    // Flags
    isPrimary: boolean('isPrimary').default(false).notNull(),
    contentAnalysis: contentAnalysisEnum('contentAnalysis')
      .default(ContentAnalysis.BOTH)
      .notNull(),
    // AI-resolved analysis path when the user chose contentAnalysis='auto'.
    // NULL for manual picks. Stores the concrete mode N8N recommended so
    // downstream stages derive is_transcribe/analyze_video correctly and the
    // UI can show "Auto → Video only". Never holds 'auto' itself.
    autoMode: contentAnalysisEnum('autoMode'),
    // Set exactly once when videoUpload minutes are charged for an AUTO-mode
    // primary language (charged at outline resolution or permanent failure, not
    // at upload). NULL = not yet charged. An atomic conditional UPDATE on this
    // column is the exactly-once billing guard.
    videoQuotaChargedAt: timestamp('videoQuotaChargedAt', {
      withTimezone: true
    }),
    skipScriptReview: boolean('skipScriptReview').default(false).notNull(),

    // Script template used for initial generation
    templateId: uuid('templateId').references(() => templates.id, {
      onDelete: 'set null'
    }),

    // System template toggle flags
    includeTransitions: boolean('includeTransitions').default(true).notNull(),
    transitionIn: text('transitionIn').default('steps').notNull(),
    isIntro: boolean('isIntro').default(true).notNull(),
    isOutro: boolean('isOutro').default(true).notNull(),

    // Cursor-driven auto-zoom. Per-language default that new scriptVersions
    // inherit from — mirrors the voice/avatar inheritance pattern so a user's
    // off-toggle survives script edits, voice changes, avatar changes, and
    // translation into a new language.
    autoZoom: boolean('autoZoom').default(true).notNull(),
    slides: boolean('slides').default(true).notNull(),

    // User attribution
    createdBy: uuid('createdBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    editedBy: uuid('editedBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    deletedBy: uuid('deletedBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
    deletedAt: timestamp('deletedAt', { withTimezone: true })
  },
  (table) => [
    index('IX_projectLanguages_projectId').using(
      'btree',
      table.projectId.asc()
    ),
    index('IX_projectLanguages_languageId').using(
      'btree',
      table.languageId.asc()
    ),
    index('IX_projectLanguages_voiceId').using('btree', table.voiceId.asc()),
    index('IX_projectLanguages_sharedVoiceId').using(
      'btree',
      table.sharedVoiceId.asc()
    ),
    index('IX_projectLanguages_customVoiceId').using(
      'btree',
      table.customVoiceId.asc()
    ),
    index('IX_projectLanguages_avatarId').using('btree', table.avatarId.asc()),
    index('IX_projectLanguages_customAvatarId').using(
      'btree',
      table.customAvatarId.asc()
    ),
    index('IX_projectLanguages_currentStage').using(
      'btree',
      table.currentStage.asc()
    ),
    index('IX_projectLanguages_stageStatus').using(
      'btree',
      table.stageStatus.asc()
    ),
    index('IX_projectLanguages_isPrimary').using(
      'btree',
      table.isPrimary.asc()
    ),
    index('IX_projectLanguages_currentScriptVersionId').using(
      'btree',
      table.currentScriptVersionId.asc()
    ),
    index('IX_projectLanguages_createdAt').using(
      'btree',
      table.createdAt.asc()
    ),
    // Unique constraint: one language per project
    uniqueIndex('IX_projectLanguages_projectId_languageId_unique').using(
      'btree',
      table.projectId.asc(),
      table.languageId.asc()
    )
  ]
);

// Assets - Stores S3 references only, all metadata in processedData (editor JSON)
export const assets = pgTable(
  'assets',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    userId: uuid('userId').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, { onDelete: 'restrict' }),

    assetType: assetTypeEnum('assetType').notNull(),

    filename: varchar('filename', { length: 255 }).notNull(),
    mimeType: varchar('mimeType', { length: 100 }).notNull(),

    sizeBytes: bigint('sizeBytes', { mode: 'number' }).notNull(), // For storage quota tracking

    s3Key: text('s3Key').notNull(), // S3 object key
    s3Url: text('s3Url').notNull(), // Public / signed URL
    // User attribution
    createdBy: uuid('createdBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    editedBy: uuid('editedBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    deletedBy: uuid('deletedBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
    deletedAt: timestamp('deletedAt', { withTimezone: true })
  },
  (table) => [
    index('IX_assets_s3Url').using('btree', table.s3Url.asc()), // Changed from unique to regular index
    index('IX_assets_userId').using('btree', table.userId.asc()),
    index('IX_assets_assetType').using('btree', table.assetType.asc())
  ]
);

// Project Assets Junction
export const projectAssets = pgTable(
  'projectAssets',
  {
    projectAssetId: uuid('projectAssetId').primaryKey().defaultRandom(),
    projectId: uuid('projectId')
      .notNull()
      .references(() => projects.id, { onDelete: 'cascade' }),
    assetId: uuid('assetId')
      .notNull()
      .references(() => assets.id, { onDelete: 'cascade' }),
    assetRole: assetRoleEnum('assetRole').notNull(),
    metadata: jsonb('metadata'),
    // User attribution
    createdBy: uuid('createdBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    editedBy: uuid('editedBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    deletedBy: uuid('deletedBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
    deletedAt: timestamp('deletedAt', { withTimezone: true })
  },
  (table) => [
    uniqueIndex('IX_projectAssets_projectId_assetId_unique').using(
      'btree',
      table.projectId.asc(),
      table.assetId.asc()
    ),
    index('IX_projectAssets_projectId').using('btree', table.projectId.asc()),
    index('IX_projectAssets_assetId').using('btree', table.assetId.asc()),
    index('IX_projectAssets_assetRole').using('btree', table.assetRole.asc())
  ]
);

// Parts
export const parts = pgTable(
  'parts',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    projectId: uuid('projectId')
      .notNull()
      .references(() => projects.id, { onDelete: 'cascade' }),
    projectLanguageId: uuid('projectLanguageId').references(
      () => projectLanguages.id,
      { onDelete: 'cascade' }
    ),
    partTitle: text('partTitle').notNull(),
    partNumber: integer('partNumber').notNull(),
    partStartIndex: integer('partStartIndex').notNull(),
    partEndIndex: integer('partEndIndex').notNull(),
    // User attribution
    createdBy: uuid('createdBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    editedBy: uuid('editedBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    deletedBy: uuid('deletedBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
    deletedAt: timestamp('deletedAt', { withTimezone: true })
  },
  (table) => [
    index('IX_parts_projectId').using('btree', table.projectId.asc()),
    index('IX_parts_projectLanguageId').using(
      'btree',
      table.projectLanguageId.asc()
    ),
    // Unique constraint per language - each projectLanguage can have its own parts
    uniqueIndex('IX_parts_projectLanguageId_partNumber_unique').using(
      'btree',
      table.projectLanguageId.asc(),
      table.partNumber.asc()
    )
  ]
);

// Steps
export const steps = pgTable(
  'steps',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    partId: uuid('partId')
      .notNull()
      .references(() => parts.id, { onDelete: 'cascade' }),
    projectLanguageId: uuid('projectLanguageId').references(
      () => projectLanguages.id,
      { onDelete: 'cascade' }
    ),
    stepNumber: integer('stepNumber').notNull(),
    stepDescription: text('stepDescription').notNull(),
    startIndex: integer('startIndex').notNull(),
    endIndex: integer('endIndex').notNull(),
    // User attribution
    createdBy: uuid('createdBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    editedBy: uuid('editedBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    deletedBy: uuid('deletedBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
    deletedAt: timestamp('deletedAt', { withTimezone: true })
  },
  (table) => [
    index('IX_steps_partId').using('btree', table.partId.asc()),
    index('IX_steps_projectLanguageId').using(
      'btree',
      table.projectLanguageId.asc()
    ),
    uniqueIndex('IX_steps_partId_stepNumber_unique').using(
      'btree',
      table.partId.asc(),
      table.stepNumber.asc()
    )
  ]
);
// Actions
export const actions = pgTable(
  'actions',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    stepId: uuid('stepId')
      .notNull()
      .references(() => steps.id, { onDelete: 'cascade' }),
    projectLanguageId: uuid('projectLanguageId').references(
      () => projectLanguages.id,
      { onDelete: 'cascade' }
    ),
    actionIndex: integer('actionIndex').notNull(),
    description: text('description').notNull(),
    timestamp: varchar('timestamp', { length: 50 }),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
    deletedAt: timestamp('deletedAt', { withTimezone: true })
  },
  (table) => [
    index('IX_actions_stepId').using('btree', table.stepId.asc()),
    index('IX_actions_projectLanguageId').using(
      'btree',
      table.projectLanguageId.asc()
    ),
    uniqueIndex('IX_actions_stepId_actionIndex_unique').using(
      'btree',
      table.stepId.asc(),
      table.actionIndex.asc()
    )
  ]
);

// Transcripts - stores audio transcript lines from video for script generation
export const transcripts = pgTable(
  'transcripts',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    stepId: uuid('stepId')
      .notNull()
      .references(() => steps.id, { onDelete: 'cascade' }),
    projectLanguageId: uuid('projectLanguageId').references(
      () => projectLanguages.id,
      { onDelete: 'cascade' }
    ),
    transcriptIndex: integer('transcriptIndex').notNull(), // Global index from N8N (e.g., 1, 2, 3...)
    startIndex: integer('startIndex').notNull(), // Start index for range matching
    endIndex: integer('endIndex').notNull(), // End index for range matching
    text: text('text').notNull(),
    timestamp: varchar('timestamp', { length: 50 }), // Time range like "0:00.9 - 0:03.4"
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
    deletedAt: timestamp('deletedAt', { withTimezone: true })
  },
  (table) => [
    index('IX_transcripts_stepId').using('btree', table.stepId.asc()),
    index('IX_transcripts_projectLanguageId').using(
      'btree',
      table.projectLanguageId.asc()
    )
  ]
);

// Webhook Jobs
export const webhookJobs = pgTable(
  'webhookJobs',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    // projectId is nullable for IMAGE_VERIFICATION jobs (verification happens before project creation)
    projectId: uuid('projectId').references(() => projects.id, {
      onDelete: 'cascade'
    }),
    projectLanguageId: uuid('projectLanguageId').references(
      () => projectLanguages.id,
      { onDelete: 'cascade' }
    ),
    webhookType: webhookCallTypeEnum('webhookType').notNull(),
    status: jobStatusEnum('status').notNull().default(JobStatus.QUEUED),
    webhookExecutionId: text('webhookExecutionId').notNull(),
    requestPayload: jsonb('requestPayload'), // What was sent
    responsePayload: jsonb('responsePayload'), // What was received
    executionTimeMs: integer('executionTimeMs'), // Full pipeline duration in milliseconds
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
    deletedAt: timestamp('deletedAt', { withTimezone: true })
  },
  (table) => [
    index('IX_webhookJobs_projectId').using('btree', table.projectId.asc()),
    index('IX_webhookJobs_projectLanguageId').using(
      'btree',
      table.projectLanguageId.asc()
    ),
    index('IX_webhookJobs_webhookExecutionId').using(
      'btree',
      table.webhookExecutionId.asc()
    ),
    index('IX_webhookJobs_status').using('btree', table.status.asc()),
    index('IX_webhookJobs_createdAt').using('btree', table.createdAt.asc())
  ]
);
// Scripts
export const scripts = pgTable(
  'scripts',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    stepId: uuid('stepId')
      .notNull()
      .references(() => steps.id, { onDelete: 'cascade' }),
    projectId: uuid('projectId').references(() => projects.id, {
      onDelete: 'cascade'
    }),
    projectLanguageId: uuid('projectLanguageId').references(
      () => projectLanguages.id,
      { onDelete: 'cascade' }
    ),
    // Script version this script belongs to - enables version isolation
    // Each version has its own copy of scripts, allowing independent editing per version
    scriptVersionId: uuid('scriptVersionId').references(
      () => scriptVersions.id,
      { onDelete: 'cascade' }
    ),
    videoStartTime: text('videoStartTime').notNull(), // Changed to text to store time format like "1:10.5"
    videoEndTime: text('videoEndTime').notNull(), // Changed to text to store time format like "0:35"
    scriptText: text('scriptText'),
    scriptLineIndex: integer('scriptLineIndex').notNull(), // Order of script line within the step
    comments: text('comments'), // User comments/notes for this script line
    // User attribution
    createdBy: uuid('createdBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    editedBy: uuid('editedBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    deletedBy: uuid('deletedBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
    deletedAt: timestamp('deletedAt', { withTimezone: true })
  },
  (table) => [
    index('IX_scripts_stepId').using('btree', table.stepId.asc()),
    index('IX_scripts_projectLanguageId').using(
      'btree',
      table.projectLanguageId.asc()
    ),
    index('IX_scripts_scriptVersionId').using(
      'btree',
      table.scriptVersionId.asc()
    )
  ]
);
// Script Versions
export const scriptVersions = pgTable(
  'scriptVersions',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    projectId: uuid('projectId')
      .notNull()
      .references(() => projects.id, { onDelete: 'cascade' }),
    projectLanguageId: uuid('projectLanguageId')
      .notNull()
      .references(() => projectLanguages.id, { onDelete: 'cascade' }),
    voiceId: uuid('voiceId').references(() => voiceOptions.id, {
      onDelete: 'cascade'
    }),
    sharedVoiceId: uuid('sharedVoiceId').references(() => sharedVoices.id, {
      onDelete: 'cascade'
    }),
    // BYOK custom voice — copied from projectLanguage on version create, can be changed per version
    customVoiceId: uuid('customVoiceId').references(() => customVoices.id, {
      onDelete: 'set null'
    }),
    // Parent version reference - for tracking version lineage
    // When editing a CURRENT/ARCHIVED version, a new DRAFT is created with parentVersionId
    // pointing to the source version. This enables:
    // - Version history tracking
    // - Inheriting scripts from parent until modified
    // - Easier diff calculation between versions
    parentVersionId: uuid('parentVersionId'),
    versionNumber: integer('versionNumber').notNull(),
    // Unified step-level data: scripts grouped by step with audio log references
    // Structure: { stepId: { scripts: { lineIndex: scriptId }, audioLogId: string | null } }
    // Example: { "step-abc": { scripts: { "1": "script-123", "2": "script-456" }, audioLogId: "log-xyz" } }
    stepData: jsonb('stepData').notNull().$type<StepData>().default({}),
    status: versionStatusEnum('status').notNull().default(VersionStatus.DRAFT),

    // Pipeline stage tracking (moved from projectLanguages for version-level processing)
    currentStage: scriptVersionStageEnum('currentStage')
      .notNull()
      .default(ScriptVersionStage.SCRIPT),
    stageStatus: stageStatusEnum('stageStatus')
      .notNull()
      .default(StageStatus.PENDING),
    stageErrorMessage: text('stageErrorMessage'),

    // TTS model for this version (e.g. 'eleven_v3', 'eleven_multilingual_v2')
    modelId: varchar('modelId', { length: 50 }),

    // Version-specific settings
    skipSilences: boolean('skipSilences').notNull().default(false),
    autoZoom: boolean('autoZoom').notNull().default(true),
    slides: boolean('slides').notNull().default(true),
    // Track if this version was created due to a voice change on a CURRENT/ARCHIVE version
    // When true, full audio regeneration is required for all steps
    voiceChanged: boolean('voiceChanged').notNull().default(false),
    // Track why audio regeneration is needed for this version
    regenerationReason: regenerationReasonEnum('regenerationReason'),
    // Track if user has manually edited and saved the editor JSON
    // When true, the Skip Silences toggle is hidden to prevent overwriting manual edits
    hasManualEdits: boolean('hasManualEdits').notNull().default(false),

    // Run state of the Whisper word-caption job (apps/dashboard/actions/captions/*,
    // lib/queue/handlers/process-word-captions.ts). NOT a user setting and NOT part
    // of Version Field Inheritance — never copy to new versions or projectLanguages.
    // Whether a version still needs word captions is derived from its audio logs
    // (audioGenerationLogs.alignmentData.method); these only track the last job run.
    wordCaptionsStatus: stageStatusEnum('wordCaptionsStatus'),
    wordCaptionsUpdatedAt: timestamp('wordCaptionsUpdatedAt', {
      withTimezone: true,
    }),

    // Version-specific editor JSON (built during JSON_BUILD stage)
    editorJson: jsonb('editorJson'),
    // Snapshot of the first auto-generated editor JSON, used to restore original state
    originalEditorJson: jsonb('originalEditorJson'),

    // HeyGen AI Avatar placement config: selected avatar + intro/outro/transition clips
    // Structure: { avatarId, intro: {...}, outro: {...}, transitions: [...] }
    avatarPlacements: jsonb('avatarPlacements'),

    // AI avatar selection (mirrors voice — copied from projectLanguage on version create, can be changed per version)
    avatarId: uuid('avatarId').references(() => avatarOptions.id, {
      onDelete: 'set null'
    }),
    customAvatarId: uuid('customAvatarId').references(() => customAvatars.id, {
      onDelete: 'set null'
    }),
    avatarUseIntro: boolean('avatarUseIntro').notNull().default(false),
    avatarUseOutro: boolean('avatarUseOutro').notNull().default(false),
    avatarUseTransitions: boolean('avatarUseTransitions')
      .notNull()
      .default(false),
    // HeyGen v3 quality tier — copied from projectLanguage on version create,
    // can be changed per version.
    // NOTE: DB default deliberately 'STANDARD' vs app-level 'HIGH' — see the
    // matching note on projectLanguages.avatarQuality; never trust the column
    // default from an insert site.
    avatarQuality: avatarQualityTierEnum('avatarQuality')
      .notNull()
      .default('STANDARD'),

    // Set the first time rebuildEditorJsonWithAvatars succeeds for this version.
    // Acts as a durable "already appended" flag so the editor's mount-time
    // auto-append never re-fires after a save/remount and clobbers user edits.
    avatarsAppendedAt: timestamp('avatarsAppendedAt', { withTimezone: true }),

    // Script template used for this version's generation
    templateId: uuid('templateId').references(() => templates.id, {
      onDelete: 'set null'
    }),

    // Snapshot of language config at version creation time
    includeTransitions: boolean('includeTransitions').notNull().default(true),
    transitionIn: text('transitionIn').notNull().default('steps'),
    isIntro: boolean('isIntro').notNull().default(true),
    isOutro: boolean('isOutro').notNull().default(true),

    // Publishing workflow
    publishedAt: timestamp('publishedAt', { withTimezone: true }),
    publishedBy: uuid('publishedBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),

    // User attribution
    createdBy: uuid('createdBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    editedBy: uuid('editedBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    deletedBy: uuid('deletedBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    currentRenderId: uuid('currentRenderId'),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
    deletedAt: timestamp('deletedAt', { withTimezone: true })
  },
  (table) => [
    index('IX_scriptVersions_projectId').using('btree', table.projectId.asc()),
    index('IX_scriptVersions_projectLanguageId').using(
      'btree',
      table.projectLanguageId.asc()
    ),
    index('IX_scriptVersions_status').using('btree', table.status.asc()),
    index('IX_scriptVersions_currentRenderId').using(
      'btree',
      table.currentRenderId.asc()
    ),
    index('IX_scriptVersions_currentStage').using(
      'btree',
      table.currentStage.asc()
    ),
    index('IX_scriptVersions_stageStatus').using(
      'btree',
      table.stageStatus.asc()
    ),
    index('IX_scriptVersions_parentVersionId').using(
      'btree',
      table.parentVersionId.asc()
    ),
    index('IX_scriptVersions_sharedVoiceId').using(
      'btree',
      table.sharedVoiceId.asc()
    ),
    index('IX_scriptVersions_customVoiceId').using(
      'btree',
      table.customVoiceId.asc()
    ),
    index('IX_scriptVersions_avatarId').using('btree', table.avatarId.asc()),
    index('IX_scriptVersions_customAvatarId').using(
      'btree',
      table.customAvatarId.asc()
    ),
    index('IX_scriptVersions_createdAt').using('btree', table.createdAt.asc()),
    uniqueIndex(
      'IX_scriptVersions_projectLanguageId_versionNumber_unique'
    ).using('btree', table.projectLanguageId.asc(), table.versionNumber.asc())
  ]
);

// Script Edit History - Tracks granular edits for audit trail and UI display
export const scriptEditHistory = pgTable(
  'scriptEditHistory',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    scriptVersionId: uuid('scriptVersionId')
      .notNull()
      .references(() => scriptVersions.id, { onDelete: 'cascade' }),
    scriptId: uuid('scriptId')
      .notNull()
      .references(() => scripts.id, { onDelete: 'cascade' }),
    stepId: uuid('stepId')
      .notNull()
      .references(() => steps.id, { onDelete: 'cascade' }),

    // Change tracking
    previousText: text('previousText'),
    newText: text('newText'),
    changeType: scriptEditTypeEnum('changeType').notNull(),
    scriptTag: text('scriptTag'), // 'intro' | 'outro' | 'transition-in' | 'transition-out' | null

    // User attribution
    editedBy: uuid('editedBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    editedAt: timestamp('editedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
  },
  (table) => [
    index('IX_scriptEditHistory_scriptVersionId').using(
      'btree',
      table.scriptVersionId.asc()
    ),
    index('IX_scriptEditHistory_scriptId').using('btree', table.scriptId.asc()),
    index('IX_scriptEditHistory_stepId').using('btree', table.stepId.asc()),
    index('IX_scriptEditHistory_editedBy').using('btree', table.editedBy.asc()),
    index('IX_scriptEditHistory_editedAt').using('btree', table.editedAt.asc())
  ]
);

// Templates - Script generation templates (system-provided and custom)
export const templates = pgTable(
  'templates',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    templateName: text('templateName').notNull(),
    templateType: templateTypeEnum('templateType')
      .notNull()
      .default(TemplateType.CUSTOM),
    visibility: templateVisibilityEnum('visibility')
      .notNull()
      .default(TemplateVisibility.PRIVATE),
    userId: uuid('userId').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    organizationId: uuid('organizationId').references(
      () => organizationTable.id,
      { onDelete: 'restrict' }
    ),
    introPrompt: text('introPrompt'),
    scriptPrompt: text('scriptPrompt'),
    conclusionPrompt: text('conclusionPrompt'),
    transitionPrompt: text('transitionPrompt'),
    singleScriptPrompt: text('singleScriptPrompt'),
    description: text('description'),
    sortOrder: integer('sortOrder').notNull().default(0),

    createdBy: uuid('createdBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    modifiedBy: uuid('modifiedBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
    deletedAt: timestamp('deletedAt', { withTimezone: true })
  },
  (table) => [
    index('IX_templates_organizationId').using(
      'btree',
      table.organizationId.asc()
    ),
    index('IX_templates_userId').using('btree', table.userId.asc()),
    index('IX_templates_templateType').using('btree', table.templateType.asc()),
    index('IX_templates_visibility').using('btree', table.visibility.asc())
  ]
);

// Renders
export const renders = pgTable(
  'renders',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    projectId: uuid('projectId')
      .notNull()
      .references(() => projects.id, { onDelete: 'cascade' }),
    projectLanguageId: uuid('projectLanguageId').references(
      () => projectLanguages.id,
      { onDelete: 'cascade' }
    ),
    remotionRenderId: text('remotionRenderId'),
    scriptVersionId: uuid('scriptVersionId').references(
      () => scriptVersions.id,
      { onDelete: 'cascade' }
    ),
    renderAssetId: uuid('renderAssetId').references(() => assets.id, {
      onDelete: 'set null'
    }),
    name: text('name'),
    renderUrl: text('renderUrl').notNull(),
    thumbnailUrl: text('thumbnailUrl'),
    status: renderStatusEnum('status').notNull().default(RenderStatus.QUEUED),
    renderMode: renderModeEnum('renderMode')
      .notNull()
      .default(RenderMode.LAMBDA),
    durationSeconds: numeric('durationSeconds', { precision: 10, scale: 2 }),
    fileSizeBytes: bigint('fileSizeBytes', { mode: 'number' }),
    renderCostUsd: numeric('renderCostUsd', { precision: 10, scale: 6 }),
    errorMessage: text('errorMessage'),
    editorJsonSnapshot: jsonb('editorJsonSnapshot'),
    lambdaMemorySizeMb: integer('lambdaMemorySizeMb'),
    renderStartedAt: timestamp('renderStartedAt', { withTimezone: true }),
    renderCompletedAt: timestamp('renderCompletedAt', { withTimezone: true }),
    // User attribution
    createdBy: uuid('createdBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    editedBy: uuid('editedBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    deletedBy: uuid('deletedBy').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
    deletedAt: timestamp('deletedAt', { withTimezone: true })
  },
  (table) => [
    index('IX_renders_projectId').using('btree', table.projectId.asc()),
    index('IX_renders_projectLanguageId').using(
      'btree',
      table.projectLanguageId.asc()
    ),
    index('IX_renders_remotionRenderId').using(
      'btree',
      table.remotionRenderId.asc()
    ),
    index('IX_renders_scriptVersionId').using(
      'btree',
      table.scriptVersionId.asc()
    ),
    index('IX_renders_status').using('btree', table.status.asc()),
    index('IX_renders_createdAt').using('btree', table.createdAt.asc())
  ]
);
// Audio Generation Logs
export const audioGenerationLogs = pgTable(
  'audioGenerationLogs',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    projectId: uuid('projectId')
      .notNull()
      .references(() => projects.id, { onDelete: 'cascade' }),
    projectLanguageId: uuid('projectLanguageId').references(
      () => projectLanguages.id,
      { onDelete: 'cascade' }
    ),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, { onDelete: 'restrict' }),

    stepId: uuid('stepId')
      .notNull()
      .references(() => steps.id, { onDelete: 'cascade' }),
    scriptVersionId: uuid('scriptVersionId').references(
      () => scriptVersions.id,
      {
        onDelete: 'set null'
      }
    ),
    userId: uuid('userId').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    voiceId: uuid('voiceId').references(() => voiceOptions.id, {
      onDelete: 'cascade'
    }),
    sharedVoiceId: uuid('sharedVoiceId').references(() => sharedVoices.id, {
      onDelete: 'cascade'
    }),
    // TTS model used for this generation (e.g. 'eleven_v3', 'eleven_multilingual_v2')
    modelId: varchar('modelId', { length: 50 }),
    scriptText: text('scriptText'),
    requestedCharacterCount: integer('requestedCharacterCount'),
    actualCharacterCount: integer('actualCharacterCount'),
    audioAssetId: uuid('audioAssetId').references(() => assets.id, {
      onDelete: 'set null'
    }),
    durationSeconds: numeric('durationSeconds', { precision: 10, scale: 2 }),
    audioUrl: text('audioUrl'),
    status: audioGenerationStatusEnum('status')
      .notNull()
      .default(AudioGenerationStatus.PENDING),
    metadata: jsonb('metadata'),
    alignmentData: jsonb('alignmentData').notNull(),
    // New tracking columns for scalability
    queuedAt: timestamp('queuedAt', { withTimezone: true }),
    startedAt: timestamp('startedAt', { withTimezone: true }),
    completedAt: timestamp('completedAt', { withTimezone: true }),
    retryCount: integer('retryCount').default(0),
    processingTimeMs: integer('processingTimeMs'),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
    deletedAt: timestamp('deletedAt', { withTimezone: true })
  },
  (table) => [
    index('IX_audioLogs_projectId').using('btree', table.projectId.asc()),
    index('IX_audioLogs_projectLanguageId').using(
      'btree',
      table.projectLanguageId.asc()
    ),
    index('IX_audioLogs_organizationId').using(
      'btree',
      table.organizationId.asc()
    ),
    index('IX_audioLogs_stepId').using('btree', table.stepId.asc()),
    index('IX_audioLogs_userId').using('btree', table.userId.asc()),
    index('IX_audioLogs_status').using('btree', table.status.asc()),
    // Compound indexes for better query performance
    index('IX_audioLogs_projectId_status').using(
      'btree',
      table.projectId.asc(),
      table.status.asc()
    ),
    index('IX_audioLogs_projectLanguageId_status').using(
      'btree',
      table.projectLanguageId.asc(),
      table.status.asc()
    ),
    index('IX_audioLogs_organizationId_status').using(
      'btree',
      table.organizationId.asc(),
      table.status.asc()
    ),
    index('IX_audioGenerationLogs_sharedVoiceId').using(
      'btree',
      table.sharedVoiceId.asc()
    ),
    index('IX_audioLogs_createdAt').using('btree', table.createdAt.asc())
  ]
);

// Organization Limits
export const organizationLimits = pgTable(
  'organizationLimits',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, { onDelete: 'restrict' }),
    tier: subscriptionTierEnum('tier').notNull().default(SubscriptionTier.FREE),
    hadPaidSubscription: boolean('hadPaidSubscription')
      .notNull()
      .default(false),
    // AppSumo sub-tier (1-5) when tier = APPSUMO; null for every Stripe plan.
    // The tier enum alone cannot carry it - all five AppSumo tiers share one
    // enum value and differ only in their entitlement matrix.
    appsumoTier: smallint('appsumoTier'),

    // Video limits (reset monthly)
    maxVideoUploadMinutes: integer('maxVideoUploadMinutes').notNull(),
    usedVideoUploadMinutes: numeric('usedVideoUploadMinutes', {
      precision: 10,
      scale: 2
    })
      .notNull()
      .default('0'),
    maxVideoLength: integer('maxVideoLength').notNull(),

    // Storage limits (NOT reset monthly, cumulative)
    maxStorageBytes: bigint('maxStorageBytes', { mode: 'number' }).notNull(),
    usedStorageBytes: bigint('usedStorageBytes', { mode: 'number' })
      .notNull()
      .default(0),

    // Voice-over limits (reset monthly, in minutes)
    maxVoiceOverMinutes: integer('maxVoiceOverMinutes'),
    usedVoiceOverMinutes: numeric('usedVoiceOverMinutes', {
      precision: 10,
      scale: 2
    })
      .notNull()
      .default('0'),

    // Render limits (reset monthly)
    maxRenders: integer('maxRenders').notNull(),
    usedRenders: integer('usedRenders').notNull().default(0),

    // Translation limits (reset monthly)
    maxTranslations: integer('maxTranslations').notNull().default(0),
    usedTranslations: integer('usedTranslations').notNull().default(0),

    // HeyGen AI Avatar limits (reset monthly, in minutes)
    maxAvatarMinutes: integer('maxAvatarMinutes').notNull().default(0),
    avatarMinutesUsed: numeric('avatarMinutesUsed', {
      precision: 10,
      scale: 4
    })
      .notNull()
      .default('0'),

    // Count of active custom avatar records for this organization
    maxCustomAvatars: integer('maxCustomAvatars').notNull().default(0),
    customAvatarsUsed: integer('customAvatarsUsed').notNull().default(0),

    // Seat limit — number of members allowed (owner included); synced from tier,
    // admin-overridable. Over-cap orgs (post-downgrade) are grandfathered:
    // enforcement blocks NEW invitations/acceptances only, never removes members.
    maxSeats: integer('maxSeats').notNull().default(1),

    // Admin per-org trial cap overrides. null = default cap from
    // packages/billing/src/trial.ts (TRIAL_MAX_*); ignored when not trialing.
    trialMaxVideos: integer('trialMaxVideos'),
    trialMaxVoiceOverMinutes: integer('trialMaxVoiceOverMinutes'),
    trialMaxExports: integer('trialMaxExports'),
    trialMaxTranslations: integer('trialMaxTranslations'),

    // Monthly-reset anchor for AppSumo orgs, copied from the license at binding
    // (and carried forward on upgrade) so the reset cron can compute the next
    // window from organizationLimits alone, with no join to appsumoLicenses.
    appsumoAnchorAt: timestamp('appsumoAnchorAt', { withTimezone: true }),

    monthlyResetDate: timestamp('monthlyResetDate', {
      withTimezone: true
    }).notNull(),

    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
    deletedAt: timestamp('deletedAt', { withTimezone: true })
  },
  (table) => [
    uniqueIndex('IX_orgLimits_organizationId_unique').using(
      'btree',
      table.organizationId.asc()
    ),
    index('IX_orgLimits_createdAt').using('btree', table.createdAt.asc())
  ]
);

// Credit Pack Balances — add-on credits for organizations, separate from subscription limits
export const creditPackBalances = pgTable(
  'creditPackBalances',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, { onDelete: 'restrict' }),

    // Video upload quota (minutes)
    maxVideoUploadMinutes: integer('maxVideoUploadMinutes')
      .notNull()
      .default(0),
    usedVideoUploadMinutes: numeric('usedVideoUploadMinutes', {
      precision: 10,
      scale: 2
    })
      .notNull()
      .default('0'),

    // Video-analysis quota (minutes) - AppSumo one-time bucket. Silent uploads
    // burn this first and spill into the monthly upload minutes; never resets.
    maxVideoAnalysisMinutes: integer('maxVideoAnalysisMinutes')
      .notNull()
      .default(0),
    usedVideoAnalysisMinutes: numeric('usedVideoAnalysisMinutes', {
      precision: 10,
      scale: 2
    })
      .notNull()
      .default('0'),

    // Voice-over quota (minutes)
    maxVoiceOverMinutes: integer('maxVoiceOverMinutes').notNull().default(0),
    usedVoiceOverMinutes: numeric('usedVoiceOverMinutes', {
      precision: 10,
      scale: 2
    })
      .notNull()
      .default('0'),

    // Render quota
    maxRenders: integer('maxRenders').notNull().default(0),
    usedRenders: integer('usedRenders').notNull().default(0),

    // Translation quota
    maxTranslations: integer('maxTranslations').notNull().default(0),
    usedTranslations: integer('usedTranslations').notNull().default(0),

    // Storage quota (bytes, cumulative)
    maxStorageBytes: bigint('maxStorageBytes', { mode: 'number' })
      .notNull()
      .default(0),
    usedStorageBytes: bigint('usedStorageBytes', { mode: 'number' })
      .notNull()
      .default(0),

    // AI Avatar quota (minutes, stacks on top of subscription tier quota)
    maxAvatarMinutes: integer('maxAvatarMinutes').notNull().default(0),
    usedAvatarMinutes: numeric('usedAvatarMinutes', {
      precision: 10,
      scale: 4
    })
      .notNull()
      .default('0'),

    // Custom avatar slots (permanent, stacks on top of subscription tier slots)
    maxCustomAvatars: integer('maxCustomAvatars').notNull().default(0),

    // Avatar training quotas - AppSumo one-time buckets, metered per training
    // KIND (the two kinds have very different COGS) and consumed forever per
    // new identity. Never reset; a HeyGen FAILED training refunds its unit.
    maxPhotoAvatarTrainings: integer('maxPhotoAvatarTrainings')
      .notNull()
      .default(0),
    usedPhotoAvatarTrainings: integer('usedPhotoAvatarTrainings')
      .notNull()
      .default(0),
    maxDigitalTwinTrainings: integer('maxDigitalTwinTrainings')
      .notNull()
      .default(0),
    usedDigitalTwinTrainings: integer('usedDigitalTwinTrainings')
      .notNull()
      .default(0),

    // Admin-controlled expiry — null means no expiry
    expiresAt: timestamp('expiresAt', { withTimezone: true }),

    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
    deletedAt: timestamp('deletedAt', { withTimezone: true })
  },
  (table) => [
    uniqueIndex('IX_creditPackBalances_organizationId_unique')
      .using('btree', table.organizationId.asc())
      .where(isNull(table.deletedAt))
  ]
);

// Project Feedback - Thumbs up/down feedback on scripts and rendered videos
export const projectFeedbackTable = pgTable(
  'projectFeedback',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, { onDelete: 'cascade' }),
    userId: uuid('userId').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    projectId: uuid('projectId')
      .notNull()
      .references(() => projects.id, { onDelete: 'cascade' }),
    scriptVersionId: uuid('scriptVersionId')
      .notNull()
      .references(() => scriptVersions.id, { onDelete: 'cascade' }),
    context: projectFeedbackContextEnum('context').notNull(),
    rating: projectFeedbackRatingEnum('rating').notNull(),
    tags: text('tags').array(),
    message: varchar('message', { length: 4000 }),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date())
  },
  (t) => [
    index('IX_projectFeedback_organizationId').using(
      'btree',
      t.organizationId.asc()
    ),
    index('IX_projectFeedback_projectId').using('btree', t.projectId.asc()),
    index('IX_projectFeedback_scriptVersionId').using(
      'btree',
      t.scriptVersionId.asc()
    )
  ]
);

// Testimonial Rewards - One row per Senja testimonial we have seen, rewarded or
// not. Simultaneously the idempotency ledger for the CSV import, the admin
// review queue, and the guarantee that an organization is rewarded at most
// once.
//
// Append-only on purpose: no deletedAt. A soft-deleted row would drop out of
// the partial unique index below and silently re-open a second grant for an
// organization that already collected. Precedent: projectFeedbackTable,
// heygenUsageLog.
export const testimonialRewards = pgTable(
  'testimonialRewards',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    // Dedupe key. Senja's CSV export carries no testimonial id, so the import
    // derives a stable hash of (submitted date + name + email + body).
    // Re-importing the same export hits ON CONFLICT DO NOTHING and is a no-op.
    sourceKey: text('sourceKey').notNull(),
    // Nullable: Senja forms do not require an email, and real exports contain
    // rows with none. Those queue as NO_EMAIL. Stored lowercased and trimmed
    // so matching never needs lower().
    customerEmail: varchar('customerEmail', { length: 255 }),
    customerName: varchar('customerName', { length: 255 }),
    customerCompany: varchar('customerCompany', { length: 255 }),
    customerTagline: varchar('customerTagline', { length: 255 }),
    // Nullable: the export omits the rating when the form did not ask for one.
    rating: integer('rating'),
    // Kept so an admin can judge an unmatched row without leaving the queue.
    testimonialText: text('testimonialText'),
    submittedAt: timestamp('submittedAt', { withTimezone: true }),
    status: testimonialRewardStatusEnum('status')
      .notNull()
      .default(TestimonialRewardStatus.PENDING_REVIEW),
    reviewReason: testimonialRewardReviewReasonEnum('reviewReason'),
    // 0 = no VideoMule user with that email, >1 = ambiguous. Both queue.
    matchedOrganizationCount: integer('matchedOrganizationCount')
      .notNull()
      .default(0),
    organizationId: uuid('organizationId').references(
      () => organizationTable.id,
      { onDelete: 'restrict' }
    ),
    grantedAt: timestamp('grantedAt', { withTimezone: true }),
    // Null when the system decided; set when an admin resolved it by hand.
    resolvedByUserId: uuid('resolvedByUserId').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    // The grant stands even if the thank-you email fails; null here with
    // grantedAt set is what drives the queue's "resend email" action.
    rewardEmailSentAt: timestamp('rewardEmailSentAt', { withTimezone: true }),
    note: text('note'),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date())
  },
  (t) => [
    uniqueIndex('IX_testimonialRewards_sourceKey_unique').using(
      'btree',
      t.sourceKey.asc()
    ),
    // One reward per organization, ever - enforced by Postgres, not by a read.
    // NULL organizationId is ignored by unique indexes, so queued and skipped
    // rows never collide; only a second GRANTED row for one org raises 23505.
    uniqueIndex('IX_testimonialRewards_organizationId_granted_unique')
      .using('btree', t.organizationId.asc())
      .where(sql`"status" = 'GRANTED'`),
    index('IX_testimonialRewards_status').using('btree', t.status.asc()),
    index('IX_testimonialRewards_customerEmail').using(
      'btree',
      t.customerEmail.asc()
    )
  ]
);

// Avatar Options - HeyGen stock avatar library
export const avatarOptions = pgTable(
  'avatarOptions',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    heygenAvatarId: varchar('heygenAvatarId', { length: 100 }).notNull(),
    // HeyGen avatar-group id — the REAL character identity (one group = one
    // person's looks). Grouping keys off this; the name-prefix derivation is
    // only a fallback for rows synced before this column existed.
    heygenGroupId: varchar('heygenGroupId', { length: 100 }),
    displayName: varchar('displayName', { length: 255 }).notNull(),
    characterName: varchar('character_name', { length: 100 }),
    thumbnailUrl: text('thumbnailUrl'),
    previewVideoUrl: text('previewVideoUrl'),
    // Our mirrored S3/CDN copy of the signed HeyGen asset above (signed URLs
    // expire ~2-3 days after sync). Read paths serve coalesce(s3, source);
    // the sync upsert NULLs these when the source URL's path rotates so the
    // mirror job re-mirrors the new artwork.
    thumbnailS3Url: text('thumbnailS3Url'),
    previewVideoS3Url: text('previewVideoS3Url'),
    gender: varchar('gender', { length: 20 }),
    isActive: boolean('isActive').notNull().default(true),
    // Set when an admin explicitly hid this look (per-look toggle or the bulk
    // "hide old-version avatars" action). The catalog sync preserves it: a
    // look returning from HeyGen reactivates only when this is NULL. Cleared
    // when an admin re-shows the look. `isActive` stays the single gate all
    // read paths use — this column is only consulted by the writers.
    adminHiddenAt: timestamp('adminHiddenAt', { withTimezone: true }),
    // HeyGen v3 catalog metadata (`GET /v3/avatars/looks`). Empty array /
    // null = not yet synced from the v3 catalog.
    supportedApiEngines: text('supportedApiEngines')
      .array()
      .notNull()
      .default(sql`'{}'::text[]`),
    // 'studio_avatar' | 'photo_avatar' | 'digital_twin' — nullable until synced
    avatarType: varchar('avatarType', { length: 30 }),
    lastSyncedAt: timestamp('lastSyncedAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
    deletedAt: timestamp('deletedAt', { withTimezone: true })
  },
  (table) => [
    uniqueIndex('IX_avatarOptions_heygenAvatarId_unique')
      .on(table.heygenAvatarId)
      .where(isNull(table.deletedAt)),
    index('IX_avatarOptions_gender').using('btree', table.gender.asc()),
    index('IX_avatarOptions_isActive').using('btree', table.isActive.asc()),
    index('IX_avatarOptions_characterName').using(
      'btree',
      table.characterName.asc()
    )
  ]
);

// Custom Avatars - Org-scoped user-uploaded avatars
export const customAvatars = pgTable(
  'customAvatars',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, { onDelete: 'restrict' }),
    heygenAvatarId: varchar('heygenAvatarId', { length: 100 }),
    uploadedByUserId: uuid('uploadedByUserId')
      .notNull()
      .references(() => userTable.id, { onDelete: 'restrict' }),
    displayName: varchar('displayName', { length: 255 }).notNull(),
    characterName: varchar('character_name', { length: 100 }),
    gender: varchar('gender', { length: 20 }),
    primaryLanguage: varchar('primaryLanguage', { length: 20 }),
    thumbnailUrl: text('thumbnailUrl'),
    // Our mirrored S3/CDN copy — see avatarOptions.thumbnailS3Url. Settle
    // paths NULL it (via nextMirrorState) when HeyGen hands back a new asset
    // path; the mirror job then re-mirrors.
    thumbnailS3Url: text('thumbnailS3Url'),
    status: customAvatarStatusEnum('status').notNull().default('PENDING'),
    isActive: boolean('isActive').notNull().default(true),
    // HeyGen v3 catalog metadata. Backfilled to 'photo_avatar' for existing
    // rows (all pre-migration custom avatars are photo uploads).
    supportedApiEngines: text('supportedApiEngines')
      .array()
      .notNull()
      .default(sql`'{}'::text[]`),
    // 'studio_avatar' | 'photo_avatar' | 'digital_twin'
    avatarType: varchar('avatarType', { length: 30 }),
    // The training kind REQUESTED at insert: 'photo' | 'digital_twin'.
    // Distinct from avatarType, which HeyGen only reports back after the
    // training call returns - the per-kind AppSumo bucket has to be charged in
    // the same transaction as the placeholder insert, before that answer.
    trainingKind: varchar('trainingKind', { length: 20 }),
    // One-shot claim flags for the AppSumo per-kind training buckets. Set when
    // the training unit is consumed / given back, so the four FAILED paths can
    // never double-refund. Deletion never refunds.
    trainingChargedAt: timestamp('trainingChargedAt', { withTimezone: true }),
    trainingRefundedAt: timestamp('trainingRefundedAt', { withTimezone: true }),
    // HeyGen avatar-group id (a group = one identity holding looks). Needed
    // for the consent flow and for adding looks to the same identity later.
    heygenGroupId: varchar('heygenGroupId', { length: 100 }),
    // Raw HeyGen group consent_status: 'pending' | 'approved'; null = consent
    // not required (photo avatars never need it).
    consentStatus: varchar('consentStatus', { length: 20 }),
    // Voice HeyGen auto-clones from digital-twin training footage. Stored for
    // a future "speak with the avatar's own voice" mode — NOT surfaced in the
    // voice pickers today (it's a HeyGen voice, not an ElevenLabs one).
    defaultVoiceId: varchar('defaultVoiceId', { length: 100 }),
    // Why training FAILED (from the HeyGen webhook/poll) — shown in the
    // Avatar Library so users know what to fix before retrying.
    failureReason: text('failureReason'),
    previewVideoUrl: text('previewVideoUrl'),
    previewVideoS3Url: text('previewVideoS3Url'),
    // The uploaded training photo/footage (org asset) — kept for display and
    // future retraining. Asset soft-deletes independently.
    sourceAssetId: uuid('sourceAssetId').references(() => assets.id, {
      onDelete: 'set null'
    }),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
    deletedAt: timestamp('deletedAt', { withTimezone: true })
  },
  (table) => [
    uniqueIndex('IX_customAvatars_heygenAvatarId_unique')
      .on(table.heygenAvatarId)
      .where(isNull(table.deletedAt)),
    index('IX_customAvatars_organizationId').using(
      'btree',
      table.organizationId.asc()
    ),
    index('IX_customAvatars_status').using('btree', table.status.asc())
  ]
);

// Avatar Generation Jobs - Per-placement HeyGen render job tracking
export const avatarGenerationJobs = pgTable(
  'avatarGenerationJobs',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    scriptVersionId: uuid('scriptVersionId')
      .notNull()
      .references(() => scriptVersions.id, { onDelete: 'cascade' }),
    projectLanguageId: uuid('projectLanguageId')
      .notNull()
      .references(() => projectLanguages.id, { onDelete: 'cascade' }),
    placementType: varchar('placementType', { length: 20 }).notNull(),
    placementStepId: uuid('placementStepId').references(() => steps.id, {
      onDelete: 'set null'
    }),
    // Which script line within the step this clip was rendered for. Together with
    // placementStepId this is the (stepId, lineIndex) tuple that the stepData
    // pointer (stepData[stepId].avatarLogIds[lineIndex]) writes back into on success.
    scriptLineIndex: integer('scriptLineIndex'),
    heygenVideoId: varchar('heygenVideoId', { length: 100 }),
    heygenJobId: varchar('heygenJobId', { length: 100 }),
    avatarId: varchar('avatarId', { length: 100 }).notNull(),
    scriptText: text('scriptText').notNull(),
    clipDurationSeconds: numeric('clipDurationSeconds', {
      precision: 10,
      scale: 2
    }).notNull(),
    audioUrl: varchar('audioUrl', { length: 500 }),
    status: avatarGenerationJobStatusEnum('status')
      .notNull()
      .default('PENDING'),
    statusMessage: text('statusMessage'),
    // FK to the rendered MP4 asset (assets.assetType = 'video', projectAssets
    // role = STEP_VIDEO). The asset row carries s3Key, s3Url, sizeBytes.
    // Set on finalize success; on cross-version pointer reuse, this points at
    // the source job's existing asset so no duplicate asset / storage bump.
    outputAssetId: uuid('outputAssetId').references(() => assets.id, {
      onDelete: 'set null'
    }),
    durationSeconds: numeric('durationSeconds', { precision: 10, scale: 2 }),
    retryCount: integer('retryCount').notNull().default(0),
    startedAt: timestamp('startedAt', { withTimezone: true }),
    completedAt: timestamp('completedAt', { withTimezone: true }),
    processingTimeMs: integer('processingTimeMs'),
    // When this row was inserted as a copy of a COMPLETED clip from a previous
    // scriptVersion (cross-version reuse — same audio + avatar + line tag), this
    // column points to the original job for audit + debug purposes. NULL for
    // jobs that ran HeyGen themselves.
    reusedFromJobId: uuid('reusedFromJobId'),
    // True when the audio-crop Lambda was unavailable for a multi-line step
    // and we fell back to sending the FULL step audio to HeyGen. HeyGen bills
    // by audio length, so each fallback overpays vs. cropped audio. Tracked
    // per-job so the admin "Avatar Usage" page can surface the rate.
    audioCropFallback: boolean('audioCropFallback').notNull().default(false),

    // HeyGen v3 quality/engine snapshot — resolved at job insert, re-verified
    // at submit, burned from this snapshot at completion (never recomputed).
    // engine stays nullable only for rows inserted before dispatch resolved it.
    qualityTier: avatarQualityTierEnum('qualityTier')
      .notNull()
      .default('STANDARD'),
    engine: heygenEngineEnum('engine'),
    resolution: varchar('resolution', { length: 10 }),
    // 'studio_avatar' | 'photo_avatar' | 'digital_twin' — snapshot of the
    // look's avatarType at resolve time; nullable for rows predating this
    // column and for looks whose type was never synced. Together with
    // engine this drives the type-aware burn rate (HEYGEN_COST_PER_MIN_USD).
    avatarType: varchar('avatarType', { length: 30 }),
    burnMultiplier: numeric('burnMultiplier', { precision: 4, scale: 2 })
      .notNull()
      .default('1'),
    // Written at completion: Math.round(clipMinutes * multiplier * 10_000) / 10_000
    burnedMinutes: numeric('burnedMinutes', { precision: 10, scale: 4 }),

    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
    deletedAt: timestamp('deletedAt', { withTimezone: true })
  },
  (table) => [
    index('IX_avatarGenerationJobs_scriptVersionId').using(
      'btree',
      table.scriptVersionId.asc()
    ),
    index('IX_avatarGenerationJobs_projectLanguageId').using(
      'btree',
      table.projectLanguageId.asc()
    ),
    index('IX_avatarGenerationJobs_status').using('btree', table.status.asc()),
    index('IX_avatarGenerationJobs_heygenJobId').using(
      'btree',
      table.heygenJobId.asc()
    ),
    index('IX_avatarGenerationJobs_reusedFromJobId').using(
      'btree',
      table.reusedFromJobId.asc()
    ),
    index('IX_avatarGenerationJobs_scriptLineIndex').using(
      'btree',
      table.scriptLineIndex.asc()
    )
  ]
);

// HeyGen Usage Log - Cost and quota tracking for avatar generation
export const heygenUsageLog = pgTable(
  'heygenUsageLog',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, { onDelete: 'restrict' }),
    avatarGenerationJobId: uuid('avatarGenerationJobId')
      .unique()
      .references(() => avatarGenerationJobs.id, { onDelete: 'set null' }),
    clipDurationSeconds: numeric('clipDurationSeconds', {
      precision: 10,
      scale: 4
    }),
    clipDurationMinutes: numeric('clipDurationMinutes', {
      precision: 10,
      scale: 4
    }),
    estimatedCreditsUsed: numeric('estimatedCreditsUsed', {
      precision: 12,
      scale: 4
    }),
    estimatedCostUsd: numeric('estimatedCostUsd', { precision: 12, scale: 6 }),
    heygenAvatarId: varchar('heygenAvatarId', { length: 100 }),
    heygenJobId: varchar('heygenJobId', { length: 100 }),
    // HeyGen v3 quality/engine snapshot — copied from the avatarGenerationJobs
    // row at completion. NULL on historic pre-v3 rows.
    engine: heygenEngineEnum('engine'),
    resolution: varchar('resolution', { length: 10 }),
    // 'studio_avatar' | 'photo_avatar' | 'digital_twin' — same snapshot as
    // avatarGenerationJobs.avatarType, copied at completion for audit/admin
    // analytics. NULL on historic pre-type rows.
    avatarType: varchar('avatarType', { length: 30 }),
    burnMultiplier: numeric('burnMultiplier', { precision: 4, scale: 2 }),
    burnedMinutes: numeric('burnedMinutes', { precision: 10, scale: 4 }),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow()
  },
  (table) => [
    index('IX_heygenUsageLog_organizationId').using(
      'btree',
      table.organizationId.asc()
    ),
    index('IX_heygenUsageLog_createdAt').using('btree', table.createdAt.asc())
  ]
);

// Organization Integrations — per-org BYOK (Bring Your Own Key) configuration.
// One row per (organizationId, provider) pair. Currently only 'elevenlabs' is
// supported; the provider column is reserved for future integrations.
//
// Encryption note: encryptedApiKey stores AES-256-GCM ciphertext encoded as
// base64(iv || authTag || ciphertext). The server-side encryption key is derived
// from AUTH_SECRET (base64-decoded to 32 bytes). Column type is plain `text` —
// encryption/decryption is handled entirely in the application layer; the schema
// stores opaque base64 strings.
export const organizationIntegrations = pgTable(
  'organizationIntegrations',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, { onDelete: 'cascade' }),
    // Reserved for future integrations (e.g. 'heygen', 'openai')
    provider: varchar('provider', { length: 50 })
      .notNull()
      .default('elevenlabs'),
    // AES-256-GCM ciphertext, base64-encoded: base64(iv || authTag || ciphertext)
    encryptedApiKey: text('encryptedApiKey').notNull(),
    // Last 4 chars of the cleartext key for UI display, e.g. "sk_...abc1"
    keyHint: varchar('keyHint', { length: 20 }),
    keyStatus: byokKeyStatusEnum('keyStatus').notNull().default('ACTIVE'),
    // Timestamp of last successful ElevenLabs API call with this key
    keyValidatedAt: timestamp('keyValidatedAt', { withTimezone: true }),
    // Timestamp of last voice-list refresh from ElevenLabs
    lastSyncedAt: timestamp('lastSyncedAt', { withTimezone: true }),
    // Populated when keyStatus flips to INVALID or a sync error occurs
    lastSyncError: text('lastSyncError'),
    // Who connected the key (set null when the user is removed from the org)
    connectedByUserId: uuid('connectedByUserId').references(
      () => userTable.id,
      {
        onDelete: 'set null'
      }
    ),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
    deletedAt: timestamp('deletedAt', { withTimezone: true })
  },
  (table) => [
    // One active integration per org per provider
    uniqueIndex('IX_organizationIntegrations_org_provider_unique')
      .on(table.organizationId, table.provider)
      .where(isNull(table.deletedAt)),
    index('IX_organizationIntegrations_keyStatus').using(
      'btree',
      table.keyStatus.asc()
    )
  ]
);

// Custom Voices — org-scoped BYOK voice cache.
// Each row represents one ElevenLabs voice fetched from the customer's own EL
// account via their BYOK key. Voices land READY immediately (no training needed).
// PENDING/FAILED values on customVoiceStatusEnum are retained for forward-compat.
//
// Application-layer invariant: every non-deleted row MUST have a non-null
// byokIntegrationId. The column is nullable in SQL only to permit a clean
// ALTER when the table is first created empty.
export const customVoices = pgTable(
  'customVoices',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, { onDelete: 'cascade' }),
    // FK to the BYOK integration that owns this voice. Application-enforced NOT NULL.
    byokIntegrationId: uuid('byokIntegrationId').references(
      () => organizationIntegrations.id,
      { onDelete: 'cascade' }
    ),
    // ElevenLabs voice ID from the customer's account
    elevenLabsVoiceId: varchar('elevenLabsVoiceId', { length: 50 }).notNull(),
    displayName: varchar('displayName', { length: 255 }).notNull(),
    description: text('description'),
    // ElevenLabs voice category: 'cloned' | 'professional' | 'generated'
    byokSource: varchar('byokSource', { length: 50 }),
    // ElevenLabs-hosted preview URL
    previewUrl: text('previewUrl'),
    // Language/locale metadata (optional, from EL API response)
    language: varchar('language', { length: 10 }),
    locale: varchar('locale', { length: 10 }),
    gender: varchar('gender', { length: 20 }),
    accent: varchar('accent', { length: 50 }),
    // BYOK voices enter as READY immediately; PENDING/FAILED kept for forward-compat
    status: customVoiceStatusEnum('status').notNull().default('READY'),
    statusMessage: text('statusMessage'),
    isActive: boolean('isActive').notNull().default(true),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
    deletedAt: timestamp('deletedAt', { withTimezone: true })
  },
  (table) => [
    // CRITICAL: org-scoped uniqueness — two orgs CAN share the same elevenLabsVoiceId
    // if they connect the same EL account. The invariant is per-org, not global.
    uniqueIndex('IX_customVoices_orgId_elevenLabsVoiceId_unique')
      .on(table.organizationId, table.elevenLabsVoiceId)
      .where(isNull(table.deletedAt)),
    index('IX_customVoices_organizationId').using(
      'btree',
      table.organizationId.asc()
    ),
    index('IX_customVoices_byokIntegrationId').using(
      'btree',
      table.byokIntegrationId.asc()
    ),
    index('IX_customVoices_status').using('btree', table.status.asc())
  ]
);

// AppSumo Licenses - one row per license key sold on AppSumo (base tier or
// add-on). Audit table: NO deletedAt, never soft-deleted, never purged; a
// refunded key stays as a deactivated row so a later re-activation is lossless.
export const appsumoLicenses = pgTable(
  'appsumoLicenses',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    licenseKey: varchar('licenseKey', { length: 64 }).notNull(),
    // The key this row replaced (upgrade/downgrade chain), and the key that
    // replaced this one - both plain text for the same reason as below.
    prevLicenseKey: varchar('prevLicenseKey', { length: 64 }),
    // Add-ons only: the base license this add-on stacks on. Deliberately NOT a
    // foreign key - a `migrate` event can re-parent an add-on to a base key we
    // have not received the `purchase` webhook for yet.
    parentLicenseKey: varchar('parentLicenseKey', { length: 64 }),
    supersededByKey: varchar('supersededByKey', { length: 64 }),
    kind: appsumoLicenseKindEnum('kind').notNull(),
    // Base tiers 1-5 or add-on product tiers 101-103.
    tier: smallint('tier'),
    // Vendor's display name; legacy add-ons without a product tier resolve it.
    partnerPlanName: text('partnerPlanName'),
    // Stacked purchases of one add-on may arrive as one key with N units.
    unitQuantity: integer('unitQuantity').notNull().default(1),
    status: appsumoLicenseStatusEnum('status')
      .notNull()
      .default(AppSumoLicenseStatus.INACTIVE),
    // SET NULL, not restrict: a hard-deleted org must not block the audit row,
    // and the key becomes re-bindable.
    organizationId: uuid('organizationId').references(
      () => organizationTable.id,
      { onDelete: 'set null' }
    ),
    activatedByUserId: uuid('activatedByUserId').references(
      () => userTable.id,
      { onDelete: 'set null' }
    ),
    // Never changes once set; copied forward across upgrades and re-binds.
    firstActivatedAt: timestamp('firstActivatedAt', { withTimezone: true }),
    // The CURRENT binding's activation time.
    activatedAt: timestamp('activatedAt', { withTimezone: true }),
    // Monthly-reset anchor and the 60-day refund window. Both are copied
    // forward on upgrade and on re-bind - they never re-anchor.
    monthlyAnchorAt: timestamp('monthlyAnchorAt', { withTimezone: true }),
    refundWindowEndsAt: timestamp('refundWindowEndsAt', { withTimezone: true }),
    refundWindowReleasedAt: timestamp('refundWindowReleasedAt', {
      withTimezone: true
    }),
    // Written by the reconcile cron (vendor-reported status + when we asked).
    remoteCheckedAt: timestamp('remoteCheckedAt', { withTimezone: true }),
    lastEventAt: timestamp('lastEventAt', { withTimezone: true }),
    // Lifetime one-time consumption that follows the LICENSE, not the org, so
    // deleting a workspace and re-activating the key cannot refill the buckets.
    consumedOneTime: jsonb('consumedOneTime'),
    // The MONTHLY window carried across a detach - upload minutes, cloud
    // renders and that window's end. Seeded back on a re-bind only while the
    // window is still open, so deleting a workspace cannot restart the
    // recycling buckets mid-window.
    consumedMonthly: jsonb('consumedMonthly'),
    // The per-bucket grant last applied - the baseline for the delta-based
    // applyAppSumoEntitlements, so retries never stack or wipe other credits.
    appliedEntitlements: jsonb('appliedEntitlements'),
    // Vendor `created_at`, normalised to milliseconds.
    appsumoCreatedAtRaw: bigint('appsumoCreatedAtRaw', { mode: 'number' }),
    // Vendor-supplied change-plan URL; validated https + appsumo.com host at
    // write and at render, else rendered as plain text.
    licenseChangePlanUrl: varchar('licenseChangePlanUrl', { length: 2048 }),
    remoteStatus: varchar('remoteStatus', { length: 32 }),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date())
  },
  (table) => [
    uniqueIndex('IX_appsumoLicenses_licenseKey_unique').using(
      'btree',
      table.licenseKey.asc()
    ),
    // One ACTIVE BASE license per organization, enforced by Postgres rather
    // than by a read - the activation claim and the upgrade path both race.
    // NULL organizationId is ignored by unique indexes, so unbound and add-on
    // rows never collide.
    uniqueIndex('IX_appsumoLicenses_organizationId_activeBase_unique')
      .using('btree', table.organizationId.asc())
      .where(sql`"kind" = 'base' AND "status" = 'active'`),
    index('IX_appsumoLicenses_parentLicenseKey').using(
      'btree',
      table.parentLicenseKey.asc()
    ),
    index('IX_appsumoLicenses_prevLicenseKey').using(
      'btree',
      table.prevLicenseKey.asc()
    ),
    index('IX_appsumoLicenses_organizationId').using(
      'btree',
      table.organizationId.asc()
    ),
    index('IX_appsumoLicenses_activatedByUserId').using(
      'btree',
      table.activatedByUserId.asc()
    ),
    index('IX_appsumoLicenses_status').using('btree', table.status.asc())
  ]
);

// AppSumo License Events - verbatim audit log of every webhook delivery,
// reconcile decision and admin action. The row is inserted and handled in one
// transaction, so a handler failure rolls the row back and the vendor retry is
// processed rather than deduped.
export const appsumoLicenseEvents = pgTable(
  'appsumoLicenseEvents',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    licenseKey: varchar('licenseKey', { length: 64 }).notNull(),
    // Plain text on purpose - an event name we do not know yet must never make
    // the insert throw, so this is NOT an enum.
    event: varchar('event', { length: 32 }).notNull(),
    // Vendor `created_at`, normalised to milliseconds (deliveries arrive in
    // seconds or milliseconds depending on the event) - part of the dedupe key.
    appsumoCreatedAtRaw: bigint('appsumoCreatedAtRaw', {
      mode: 'number'
    }).notNull(),
    // Vendor `event_timestamp`: changes on every retry, so it is stored but
    // deliberately kept OUT of the dedupe key.
    eventTimestampRaw: bigint('eventTimestampRaw', { mode: 'number' }),
    payload: jsonb('payload').notNull(),
    signatureValid: boolean('signatureValid').notNull().default(false),
    source: appsumoEventSourceEnum('source')
      .notNull()
      .default(AppSumoEventSource.WEBHOOK),
    // Mandatory for source = 'admin'. Plain column, no FK: the audit trail must
    // survive a user hard-delete.
    actorUserId: uuid('actorUserId'),
    processedAt: timestamp('processedAt', { withTimezone: true }),
    error: text('error'),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow()
  },
  (table) => [
    // Dedupe key. `event` has to be in it: a standalone add-on's purchase and
    // activate share one created_at.
    uniqueIndex('IX_appsumoLicenseEvents_dedupe_unique').using(
      'btree',
      table.licenseKey.asc(),
      table.event.asc(),
      table.appsumoCreatedAtRaw.asc()
    ),
    index('IX_appsumoLicenseEvents_licenseKey_createdAt').using(
      'btree',
      table.licenseKey.asc(),
      table.createdAt.asc()
    )
  ]
);

// AppSumo Review Claims - one row per AppSumo-review bonus-minutes claim an
// organization submits, reviewed by hand in the admin queue. Audit ledger: NO
// deletedAt, because a soft-deleted row would drop out of the partial unique
// index and re-open a second claim (same reasoning as testimonialRewards). The
// user FKs are nullable + set null because the hard-delete cron nullifies user
// refs and then deletes the user; a restrict FK would abort that transaction.
export const appsumoReviewClaims = pgTable(
  'appsumoReviewClaims',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, { onDelete: 'cascade' }),
    submittedByUserId: uuid('submittedByUserId').references(
      () => userTable.id,
      { onDelete: 'set null' }
    ),
    // Name shown on the AppSumo review, so an admin can find it.
    appsumoDisplayName: text('appsumoDisplayName').notNull(),
    // S3 object key minted by /api/upload - never a client-supplied URL.
    screenshotKey: text('screenshotKey').notNull(),
    status: appsumoReviewClaimStatusEnum('status')
      .notNull()
      .default(AppSumoReviewClaimStatus.PENDING),
    // Set on approve.
    grantedMinutes: integer('grantedMinutes'),
    reviewedByUserId: uuid('reviewedByUserId').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    reviewedAt: timestamp('reviewedAt', { withTimezone: true }),
    rejectionReason: text('rejectionReason'),
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date())
  },
  (t) => [
    // One open-or-paid claim per organization - enforced by Postgres, not by a
    // read. REJECTED rows fall outside the predicate so a rejected org can
    // resubmit; a second PENDING/APPROVED row for one org raises 23505.
    uniqueIndex('IX_appsumoReviewClaims_organizationId_open_unique')
      .using('btree', t.organizationId.asc())
      .where(sql`"status" IN ('PENDING','APPROVED')`),
    index('IX_appsumoReviewClaims_organizationId').using(
      'btree',
      t.organizationId.asc()
    ),
    index('IX_appsumoReviewClaims_submittedByUserId').using(
      'btree',
      t.submittedByUserId.asc()
    ),
    index('IX_appsumoReviewClaims_reviewedByUserId').using(
      'btree',
      t.reviewedByUserId.asc()
    )
  ]
);

// AppSumo Review Emails - drip ledger: one row per (organization, step)
// actually sent, so the cron never sends a step twice. Append-only, NO
// deletedAt. userId is the recipient; nullable + set null for the same
// hard-delete-cron reason as the claims table.
export const appsumoReviewEmails = pgTable(
  'appsumoReviewEmails',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    organizationId: uuid('organizationId')
      .notNull()
      .references(() => organizationTable.id, { onDelete: 'cascade' }),
    // Recipient at send time, kept for support lookups; the cron keys on
    // (organizationId, step) only.
    userId: uuid('userId').references(() => userTable.id, {
      onDelete: 'set null'
    }),
    // 1 plain ask, 2 incentive, 3 last call.
    step: smallint('step').notNull(),
    sentAt: timestamp('sentAt', { withTimezone: true }).notNull().defaultNow()
  },
  (t) => [
    uniqueIndex('IX_appsumoReviewEmails_organizationId_step_unique').using(
      'btree',
      t.organizationId.asc(),
      t.step.asc()
    ),
    index('IX_appsumoReviewEmails_userId').using('btree', t.userId.asc())
  ]
);

// Relations
export const apiKeyRelations = relations(apiKeyTable, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [apiKeyTable.organizationId],
    references: [organizationTable.id]
  })
}));

export const organizationRelations = relations(
  organizationTable,
  ({ many, one }) => ({
    apiKeys: many(apiKeyTable),
    invitations: many(invitationTable),
    feedbacks: many(feedbackTable),
    webhooks: many(webhookTable),
    memberships: many(membershipTable),
    projects: many(projects),
    limits: one(organizationLimits),
    creditPackBalance: one(creditPackBalances),
    projectFeedbacks: many(projectFeedbackTable),
    favoriteVoices: many(favoriteVoices),
    customAvatars: many(customAvatars),
    customVoices: many(customVoices),
    integrations: many(organizationIntegrations),
    heygenUsageLogs: many(heygenUsageLog),
    appsumoLicenses: many(appsumoLicenses)
  })
);

export const authenticatorAppRelations = relations(
  authenticatorAppTable,
  ({ one }) => ({
    user: one(userTable, {
      fields: [authenticatorAppTable.userId],
      references: [userTable.id]
    })
  })
);

export const userRelations = relations(userTable, ({ many }) => ({
  authenticatorApps: many(authenticatorAppTable),
  changeEmailRequests: many(changeEmailRequestTable),
  accounts: many(accountTable),
  feedbacks: many(feedbackTable),
  notifications: many(notificationTable),
  sessions: many(sessionTable),
  memberships: many(membershipTable),
  projects: many(projects, { relationName: 'projectsOwner' }),
  projectsCreated: many(projects, { relationName: 'projectsCreatedBy' }),
  projectsEdited: many(projects, { relationName: 'projectsEditedBy' }),
  projectsDeleted: many(projects, { relationName: 'projectsDeletedBy' }),
  assets: many(assets, { relationName: 'assetsOwner' }),
  assetsCreated: many(assets, { relationName: 'assetsCreatedBy' }),
  assetsEdited: many(assets, { relationName: 'assetsEditedBy' }),
  assetsDeleted: many(assets, { relationName: 'assetsDeletedBy' }),
  projectAssetsCreated: many(projectAssets, {
    relationName: 'projectAssetsCreatedBy'
  }),
  projectAssetsEdited: many(projectAssets, {
    relationName: 'projectAssetsEditedBy'
  }),
  projectAssetsDeleted: many(projectAssets, {
    relationName: 'projectAssetsDeletedBy'
  }),
  partsCreated: many(parts, { relationName: 'partsCreatedBy' }),
  partsEdited: many(parts, { relationName: 'partsEditedBy' }),
  partsDeleted: many(parts, { relationName: 'partsDeletedBy' }),
  stepsCreated: many(steps, { relationName: 'stepsCreatedBy' }),
  stepsEdited: many(steps, { relationName: 'stepsEditedBy' }),
  stepsDeleted: many(steps, { relationName: 'stepsDeletedBy' }),
  scriptsCreated: many(scripts, { relationName: 'scriptsCreatedBy' }),
  scriptsEdited: many(scripts, { relationName: 'scriptsEditedBy' }),
  scriptsDeleted: many(scripts, { relationName: 'scriptsDeletedBy' }),
  scriptVersionsCreated: many(scriptVersions, {
    relationName: 'scriptVersionsCreatedBy'
  }),
  scriptVersionsEdited: many(scriptVersions, {
    relationName: 'scriptVersionsEditedBy'
  }),
  scriptVersionsDeleted: many(scriptVersions, {
    relationName: 'scriptVersionsDeletedBy'
  }),
  scriptVersionsPublished: many(scriptVersions, {
    relationName: 'scriptVersionsPublishedBy'
  }),
  rendersCreated: many(renders, { relationName: 'rendersCreatedBy' }),
  rendersEdited: many(renders, { relationName: 'rendersEditedBy' }),
  rendersDeleted: many(renders, { relationName: 'rendersDeletedBy' }),
  projectLanguagesCreated: many(projectLanguages, {
    relationName: 'projectLanguagesCreatedBy'
  }),
  projectLanguagesEdited: many(projectLanguages, {
    relationName: 'projectLanguagesEditedBy'
  }),
  projectLanguagesDeleted: many(projectLanguages, {
    relationName: 'projectLanguagesDeletedBy'
  }),
  scriptEditHistoryEdits: many(scriptEditHistory, {
    relationName: 'scriptEditHistoryEditedBy'
  }),
  projectFeedbacks: many(projectFeedbackTable),
  favoriteVoices: many(favoriteVoices)
}));

export const changeEmailRequestRelations = relations(
  changeEmailRequestTable,
  ({ one }) => ({
    user: one(userTable, {
      fields: [changeEmailRequestTable.userId],
      references: [userTable.id]
    })
  })
);

export const invitationRelations = relations(invitationTable, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [invitationTable.organizationId],
    references: [organizationTable.id]
  })
}));

export const accountRelations = relations(accountTable, ({ one }) => ({
  user: one(userTable, {
    fields: [accountTable.userId],
    references: [userTable.id]
  })
}));

export const feedbackRelations = relations(feedbackTable, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [feedbackTable.organizationId],
    references: [organizationTable.id]
  }),
  user: one(userTable, {
    fields: [feedbackTable.userId],
    references: [userTable.id]
  })
}));

export const notificationRelations = relations(
  notificationTable,
  ({ one }) => ({
    user: one(userTable, {
      fields: [notificationTable.userId],
      references: [userTable.id]
    })
  })
);

export const sessionRelations = relations(sessionTable, ({ one }) => ({
  user: one(userTable, {
    fields: [sessionTable.userId],
    references: [userTable.id]
  })
}));

export const webhookRelations = relations(webhookTable, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [webhookTable.organizationId],
    references: [organizationTable.id]
  })
}));

export const membershipRelations = relations(membershipTable, ({ one }) => ({
  user: one(userTable, {
    fields: [membershipTable.userId],
    references: [userTable.id]
  }),
  organization: one(organizationTable, {
    fields: [membershipTable.organizationId],
    references: [organizationTable.id]
  })
}));

export const organizationLogoRelations = relations(
  organizationLogoTable,
  ({ one }) => ({
    organization: one(organizationTable, {
      fields: [organizationLogoTable.organizationId],
      references: [organizationTable.id]
    })
  })
);

export const subscriptionRelations = relations(
  subscriptionTable,
  ({ one }) => ({
    organization: one(organizationTable, {
      fields: [subscriptionTable.organizationId],
      references: [organizationTable.id]
    })
  })
);

export const subscriptionItemRelations = relations(
  subscriptionItemTable,
  ({ one }) => ({
    organization: one(subscriptionTable, {
      fields: [subscriptionItemTable.subscriptionId],
      references: [subscriptionTable.id]
    })
  })
);

export const orderRelations = relations(orderTable, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [orderTable.organizationId],
    references: [organizationTable.id]
  })
}));

export const orderItemRelations = relations(orderItemTable, ({ one }) => ({
  organization: one(orderTable, {
    fields: [orderItemTable.orderId],
    references: [orderTable.id]
  })
}));

export const projectsRelations = relations(projects, ({ one, many }) => ({
  user: one(userTable, {
    fields: [projects.userId],
    references: [userTable.id],
    relationName: 'projectsOwner'
  }),
  organization: one(organizationTable, {
    fields: [projects.organizationId],
    references: [organizationTable.id]
  }),
  defaultLanguage: one(languageOptions, {
    fields: [projects.defaultLanguageId],
    references: [languageOptions.id]
  }),
  defaultVoice: one(voiceOptions, {
    fields: [projects.defaultVoiceId],
    references: [voiceOptions.id]
  }),
  // User attribution relations
  createdByUser: one(userTable, {
    fields: [projects.createdBy],
    references: [userTable.id],
    relationName: 'projectsCreatedBy'
  }),
  editedByUser: one(userTable, {
    fields: [projects.editedBy],
    references: [userTable.id],
    relationName: 'projectsEditedBy'
  }),
  deletedByUser: one(userTable, {
    fields: [projects.deletedBy],
    references: [userTable.id],
    relationName: 'projectsDeletedBy'
  }),
  parts: many(parts),
  scriptVersions: many(scriptVersions),
  renders: many(renders),
  webhookJobs: many(webhookJobs),
  audioGenerationLogs: many(audioGenerationLogs),
  projectAssets: many(projectAssets),
  projectLanguages: many(projectLanguages)
}));

// Project Languages Relations
export const projectLanguagesRelations = relations(
  projectLanguages,
  ({ one, many }) => ({
    project: one(projects, {
      fields: [projectLanguages.projectId],
      references: [projects.id]
    }),
    language: one(languageOptions, {
      fields: [projectLanguages.languageId],
      references: [languageOptions.id]
    }),
    voice: one(voiceOptions, {
      fields: [projectLanguages.voiceId],
      references: [voiceOptions.id]
    }),
    sharedVoice: one(sharedVoices, {
      fields: [projectLanguages.sharedVoiceId],
      references: [sharedVoices.id]
    }),
    customVoice: one(customVoices, {
      fields: [projectLanguages.customVoiceId],
      references: [customVoices.id]
    }),
    currentScriptVersion: one(scriptVersions, {
      fields: [projectLanguages.currentScriptVersionId],
      references: [scriptVersions.id]
    }),
    template: one(templates, {
      fields: [projectLanguages.templateId],
      references: [templates.id]
    }),
    // User attribution relations
    createdByUser: one(userTable, {
      fields: [projectLanguages.createdBy],
      references: [userTable.id],
      relationName: 'projectLanguagesCreatedBy'
    }),
    editedByUser: one(userTable, {
      fields: [projectLanguages.editedBy],
      references: [userTable.id],
      relationName: 'projectLanguagesEditedBy'
    }),
    deletedByUser: one(userTable, {
      fields: [projectLanguages.deletedBy],
      references: [userTable.id],
      relationName: 'projectLanguagesDeletedBy'
    }),
    // Pipeline relations
    parts: many(parts),
    scripts: many(scripts),
    scriptVersions: many(scriptVersions),
    audioGenerationLogs: many(audioGenerationLogs),
    avatarGenerationJobs: many(avatarGenerationJobs),
    renders: many(renders),
    webhookJobs: many(webhookJobs),
    transcripts: many(transcripts)
  })
);

export const projectAssetsRelations = relations(projectAssets, ({ one }) => ({
  project: one(projects, {
    fields: [projectAssets.projectId],
    references: [projects.id]
  }),
  asset: one(assets, {
    fields: [projectAssets.assetId],
    references: [assets.id]
  }),
  // User attribution relations
  createdByUser: one(userTable, {
    fields: [projectAssets.createdBy],
    references: [userTable.id],
    relationName: 'projectAssetsCreatedBy'
  }),
  editedByUser: one(userTable, {
    fields: [projectAssets.editedBy],
    references: [userTable.id],
    relationName: 'projectAssetsEditedBy'
  }),
  deletedByUser: one(userTable, {
    fields: [projectAssets.deletedBy],
    references: [userTable.id],
    relationName: 'projectAssetsDeletedBy'
  })
}));

export const assetsRelations = relations(assets, ({ one, many }) => ({
  user: one(userTable, {
    fields: [assets.userId],
    references: [userTable.id],
    relationName: 'assetsOwner'
  }),
  // User attribution relations
  createdByUser: one(userTable, {
    fields: [assets.createdBy],
    references: [userTable.id],
    relationName: 'assetsCreatedBy'
  }),
  editedByUser: one(userTable, {
    fields: [assets.editedBy],
    references: [userTable.id],
    relationName: 'assetsEditedBy'
  }),
  deletedByUser: one(userTable, {
    fields: [assets.deletedBy],
    references: [userTable.id],
    relationName: 'assetsDeletedBy'
  }),
  projectAssets: many(projectAssets)
}));
export const partsRelations = relations(parts, ({ one, many }) => ({
  project: one(projects, {
    fields: [parts.projectId],
    references: [projects.id]
  }),
  projectLanguage: one(projectLanguages, {
    fields: [parts.projectLanguageId],
    references: [projectLanguages.id]
  }),
  // User attribution relations
  createdByUser: one(userTable, {
    fields: [parts.createdBy],
    references: [userTable.id],
    relationName: 'partsCreatedBy'
  }),
  editedByUser: one(userTable, {
    fields: [parts.editedBy],
    references: [userTable.id],
    relationName: 'partsEditedBy'
  }),
  deletedByUser: one(userTable, {
    fields: [parts.deletedBy],
    references: [userTable.id],
    relationName: 'partsDeletedBy'
  }),
  steps: many(steps)
}));

export const stepsRelations = relations(steps, ({ one, many }) => ({
  part: one(parts, {
    fields: [steps.partId],
    references: [parts.id]
  }),
  projectLanguage: one(projectLanguages, {
    fields: [steps.projectLanguageId],
    references: [projectLanguages.id]
  }),
  // User attribution relations
  createdByUser: one(userTable, {
    fields: [steps.createdBy],
    references: [userTable.id],
    relationName: 'stepsCreatedBy'
  }),
  editedByUser: one(userTable, {
    fields: [steps.editedBy],
    references: [userTable.id],
    relationName: 'stepsEditedBy'
  }),
  deletedByUser: one(userTable, {
    fields: [steps.deletedBy],
    references: [userTable.id],
    relationName: 'stepsDeletedBy'
  }),
  actions: many(actions),
  scripts: many(scripts),
  transcripts: many(transcripts),
  scriptEditHistory: many(scriptEditHistory)
}));

export const actionsRelations = relations(actions, ({ one }) => ({
  step: one(steps, {
    fields: [actions.stepId],
    references: [steps.id]
  }),
  projectLanguage: one(projectLanguages, {
    fields: [actions.projectLanguageId],
    references: [projectLanguages.id]
  })
}));

export const transcriptsRelations = relations(transcripts, ({ one }) => ({
  step: one(steps, {
    fields: [transcripts.stepId],
    references: [steps.id]
  }),
  projectLanguage: one(projectLanguages, {
    fields: [transcripts.projectLanguageId],
    references: [projectLanguages.id]
  })
}));

export const languageOptionsRelations = relations(
  languageOptions,
  ({ many }) => ({
    projects: many(projects),
    projectLanguages: many(projectLanguages),
    v3LanguageVoices: many(v3LanguageVoices),
    languageVoiceOrder: many(languageVoiceOrder)
  })
);

export const voiceOptionsRelations = relations(voiceOptions, ({ many }) => ({
  projects: many(projects),
  projectLanguages: many(projectLanguages),
  audioGenerationLogs: many(audioGenerationLogs),
  favoriteVoices: many(favoriteVoices),
  languageVoiceOrder: many(languageVoiceOrder)
}));

export const sharedVoicesRelations = relations(sharedVoices, ({ many }) => ({
  v3LanguageVoices: many(v3LanguageVoices),
  projectLanguages: many(projectLanguages),
  scriptVersions: many(scriptVersions),
  audioGenerationLogs: many(audioGenerationLogs),
  favoriteVoices: many(favoriteVoices)
}));

export const webhookJobsRelations = relations(webhookJobs, ({ one }) => ({
  project: one(projects, {
    fields: [webhookJobs.projectId],
    references: [projects.id]
  }),
  projectLanguage: one(projectLanguages, {
    fields: [webhookJobs.projectLanguageId],
    references: [projectLanguages.id]
  })
}));

export const scriptVersionsRelations = relations(
  scriptVersions,
  ({ one, many }) => ({
    project: one(projects, {
      fields: [scriptVersions.projectId],
      references: [projects.id]
    }),
    projectLanguage: one(projectLanguages, {
      fields: [scriptVersions.projectLanguageId],
      references: [projectLanguages.id]
    }),
    voice: one(voiceOptions, {
      fields: [scriptVersions.voiceId],
      references: [voiceOptions.id]
    }),
    sharedVoice: one(sharedVoices, {
      fields: [scriptVersions.sharedVoiceId],
      references: [sharedVoices.id]
    }),
    customVoice: one(customVoices, {
      fields: [scriptVersions.customVoiceId],
      references: [customVoices.id]
    }),
    // Parent version reference for version lineage
    parentVersion: one(scriptVersions, {
      fields: [scriptVersions.parentVersionId],
      references: [scriptVersions.id],
      relationName: 'versionLineage'
    }),
    // Child versions (drafts created from this version)
    childVersions: many(scriptVersions, {
      relationName: 'versionLineage'
    }),
    // User attribution relations
    createdByUser: one(userTable, {
      fields: [scriptVersions.createdBy],
      references: [userTable.id],
      relationName: 'scriptVersionsCreatedBy'
    }),
    editedByUser: one(userTable, {
      fields: [scriptVersions.editedBy],
      references: [userTable.id],
      relationName: 'scriptVersionsEditedBy'
    }),
    deletedByUser: one(userTable, {
      fields: [scriptVersions.deletedBy],
      references: [userTable.id],
      relationName: 'scriptVersionsDeletedBy'
    }),
    publishedByUser: one(userTable, {
      fields: [scriptVersions.publishedBy],
      references: [userTable.id],
      relationName: 'scriptVersionsPublishedBy'
    }),
    template: one(templates, {
      fields: [scriptVersions.templateId],
      references: [templates.id]
    }),
    renders: one(renders, {
      fields: [scriptVersions.currentRenderId],
      references: [renders.id]
    }),
    audioGenerationLogs: many(audioGenerationLogs),
    avatarGenerationJobs: many(avatarGenerationJobs),
    editHistory: many(scriptEditHistory)
  })
);

export const scriptsRelations = relations(scripts, ({ one, many }) => ({
  step: one(steps, {
    fields: [scripts.stepId],
    references: [steps.id]
  }),
  project: one(projects, {
    fields: [scripts.projectId],
    references: [projects.id]
  }),
  projectLanguage: one(projectLanguages, {
    fields: [scripts.projectLanguageId],
    references: [projectLanguages.id]
  }),
  scriptVersion: one(scriptVersions, {
    fields: [scripts.scriptVersionId],
    references: [scriptVersions.id]
  }),
  // User attribution relations
  createdByUser: one(userTable, {
    fields: [scripts.createdBy],
    references: [userTable.id],
    relationName: 'scriptsCreatedBy'
  }),
  editedByUser: one(userTable, {
    fields: [scripts.editedBy],
    references: [userTable.id],
    relationName: 'scriptsEditedBy'
  }),
  deletedByUser: one(userTable, {
    fields: [scripts.deletedBy],
    references: [userTable.id],
    relationName: 'scriptsDeletedBy'
  }),
  editHistory: many(scriptEditHistory)
}));
export const rendersRelations = relations(renders, ({ one }) => ({
  project: one(projects, {
    fields: [renders.projectId],
    references: [projects.id]
  }),
  projectLanguage: one(projectLanguages, {
    fields: [renders.projectLanguageId],
    references: [projectLanguages.id]
  }),
  scriptVersion: one(scriptVersions, {
    fields: [renders.scriptVersionId],
    references: [scriptVersions.id]
  }),
  // User attribution relations
  createdByUser: one(userTable, {
    fields: [renders.createdBy],
    references: [userTable.id],
    relationName: 'rendersCreatedBy'
  }),
  editedByUser: one(userTable, {
    fields: [renders.editedBy],
    references: [userTable.id],
    relationName: 'rendersEditedBy'
  }),
  deletedByUser: one(userTable, {
    fields: [renders.deletedBy],
    references: [userTable.id],
    relationName: 'rendersDeletedBy'
  })
}));
export const audioGenerationLogsRelations = relations(
  audioGenerationLogs,
  ({ one }) => ({
    project: one(projects, {
      fields: [audioGenerationLogs.projectId],
      references: [projects.id]
    }),
    projectLanguage: one(projectLanguages, {
      fields: [audioGenerationLogs.projectLanguageId],
      references: [projectLanguages.id]
    }),
    scriptVersion: one(scriptVersions, {
      fields: [audioGenerationLogs.scriptVersionId],
      references: [scriptVersions.id]
    }),
    user: one(userTable, {
      fields: [audioGenerationLogs.userId],
      references: [userTable.id]
    }),
    voice: one(voiceOptions, {
      fields: [audioGenerationLogs.voiceId],
      references: [voiceOptions.id]
    }),
    sharedVoice: one(sharedVoices, {
      fields: [audioGenerationLogs.sharedVoiceId],
      references: [sharedVoices.id]
    })
  })
);

export const organizationLimitsRelations = relations(
  organizationLimits,
  ({ one }) => ({
    organization: one(organizationTable, {
      fields: [organizationLimits.organizationId],
      references: [organizationTable.id]
    })
  })
);

export const creditPackBalancesRelations = relations(
  creditPackBalances,
  ({ one }) => ({
    organization: one(organizationTable, {
      fields: [creditPackBalances.organizationId],
      references: [organizationTable.id]
    })
  })
);

// Script Edit History Relations
export const scriptEditHistoryRelations = relations(
  scriptEditHistory,
  ({ one }) => ({
    scriptVersion: one(scriptVersions, {
      fields: [scriptEditHistory.scriptVersionId],
      references: [scriptVersions.id]
    }),
    script: one(scripts, {
      fields: [scriptEditHistory.scriptId],
      references: [scripts.id]
    }),
    step: one(steps, {
      fields: [scriptEditHistory.stepId],
      references: [steps.id]
    }),
    editedByUser: one(userTable, {
      fields: [scriptEditHistory.editedBy],
      references: [userTable.id],
      relationName: 'scriptEditHistoryEditedBy'
    })
  })
);

export const templatesRelations = relations(templates, ({ one }) => ({
  user: one(userTable, {
    fields: [templates.userId],
    references: [userTable.id],
    relationName: 'templateUser'
  }),
  organization: one(organizationTable, {
    fields: [templates.organizationId],
    references: [organizationTable.id]
  }),
  createdByUser: one(userTable, {
    fields: [templates.createdBy],
    references: [userTable.id],
    relationName: 'templatesCreatedBy'
  }),
  modifiedByUser: one(userTable, {
    fields: [templates.modifiedBy],
    references: [userTable.id],
    relationName: 'templatesModifiedBy'
  })
}));

// V3 Language Voices Relations
export const v3LanguageVoicesRelations = relations(
  v3LanguageVoices,
  ({ one }) => ({
    language: one(languageOptions, {
      fields: [v3LanguageVoices.languageId],
      references: [languageOptions.id]
    }),
    voice: one(sharedVoices, {
      fields: [v3LanguageVoices.voiceId],
      references: [sharedVoices.id]
    }),
    addedByUser: one(userTable, {
      fields: [v3LanguageVoices.addedByUserId],
      references: [userTable.id]
    })
  })
);
export const projectFeedbackRelations = relations(
  projectFeedbackTable,
  ({ one }) => ({
    organization: one(organizationTable, {
      fields: [projectFeedbackTable.organizationId],
      references: [organizationTable.id]
    }),
    user: one(userTable, {
      fields: [projectFeedbackTable.userId],
      references: [userTable.id]
    }),
    project: one(projects, {
      fields: [projectFeedbackTable.projectId],
      references: [projects.id]
    }),
    scriptVersion: one(scriptVersions, {
      fields: [projectFeedbackTable.scriptVersionId],
      references: [scriptVersions.id]
    })
  })
);

export const testimonialRewardsRelations = relations(
  testimonialRewards,
  ({ one }) => ({
    organization: one(organizationTable, {
      fields: [testimonialRewards.organizationId],
      references: [organizationTable.id]
    }),
    resolvedByUser: one(userTable, {
      fields: [testimonialRewards.resolvedByUserId],
      references: [userTable.id]
    })
  })
);

export const favoriteVoicesRelations = relations(favoriteVoices, ({ one }) => ({
  user: one(userTable, {
    fields: [favoriteVoices.userId],
    references: [userTable.id]
  }),
  organization: one(organizationTable, {
    fields: [favoriteVoices.organizationId],
    references: [organizationTable.id]
  }),
  voice: one(voiceOptions, {
    fields: [favoriteVoices.voiceId],
    references: [voiceOptions.id]
  }),
  sharedVoice: one(sharedVoices, {
    fields: [favoriteVoices.sharedVoiceId],
    references: [sharedVoices.id]
  }),
  customVoice: one(customVoices, {
    fields: [favoriteVoices.customVoiceId],
    references: [customVoices.id]
  })
}));

export const languageVoiceOrderRelations = relations(
  languageVoiceOrder,
  ({ one }) => ({
    language: one(languageOptions, {
      fields: [languageVoiceOrder.languageId],
      references: [languageOptions.id]
    }),
    voiceOption: one(voiceOptions, {
      fields: [languageVoiceOrder.voiceOptionId],
      references: [voiceOptions.id]
    })
  })
);

export const customAvatarsRelations = relations(customAvatars, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [customAvatars.organizationId],
    references: [organizationTable.id]
  }),
  uploadedByUser: one(userTable, {
    fields: [customAvatars.uploadedByUserId],
    references: [userTable.id]
  })
}));

export const appsumoLicensesRelations = relations(
  appsumoLicenses,
  ({ one }) => ({
    organization: one(organizationTable, {
      fields: [appsumoLicenses.organizationId],
      references: [organizationTable.id]
    }),
    activatedByUser: one(userTable, {
      fields: [appsumoLicenses.activatedByUserId],
      references: [userTable.id]
    })
  })
);

export const appsumoReviewClaimsRelations = relations(
  appsumoReviewClaims,
  ({ one }) => ({
    organization: one(organizationTable, {
      fields: [appsumoReviewClaims.organizationId],
      references: [organizationTable.id]
    }),
    submittedByUser: one(userTable, {
      fields: [appsumoReviewClaims.submittedByUserId],
      references: [userTable.id]
    }),
    reviewedByUser: one(userTable, {
      fields: [appsumoReviewClaims.reviewedByUserId],
      references: [userTable.id]
    })
  })
);

export const appsumoReviewEmailsRelations = relations(
  appsumoReviewEmails,
  ({ one }) => ({
    organization: one(organizationTable, {
      fields: [appsumoReviewEmails.organizationId],
      references: [organizationTable.id]
    }),
    user: one(userTable, {
      fields: [appsumoReviewEmails.userId],
      references: [userTable.id]
    })
  })
);

export const customVoicesRelations = relations(
  customVoices,
  ({ one, many }) => ({
    organization: one(organizationTable, {
      fields: [customVoices.organizationId],
      references: [organizationTable.id]
    }),
    byokIntegration: one(organizationIntegrations, {
      fields: [customVoices.byokIntegrationId],
      references: [organizationIntegrations.id]
    }),
    projectLanguages: many(projectLanguages),
    scriptVersions: many(scriptVersions),
    favoriteVoices: many(favoriteVoices)
  })
);

export const organizationIntegrationsRelations = relations(
  organizationIntegrations,
  ({ one, many }) => ({
    organization: one(organizationTable, {
      fields: [organizationIntegrations.organizationId],
      references: [organizationTable.id]
    }),
    connectedByUser: one(userTable, {
      fields: [organizationIntegrations.connectedByUserId],
      references: [userTable.id]
    }),
    customVoices: many(customVoices)
  })
);

export const avatarGenerationJobsRelations = relations(
  avatarGenerationJobs,
  ({ one, many }) => ({
    scriptVersion: one(scriptVersions, {
      fields: [avatarGenerationJobs.scriptVersionId],
      references: [scriptVersions.id]
    }),
    projectLanguage: one(projectLanguages, {
      fields: [avatarGenerationJobs.projectLanguageId],
      references: [projectLanguages.id]
    }),
    placementStep: one(steps, {
      fields: [avatarGenerationJobs.placementStepId],
      references: [steps.id]
    }),
    heygenUsageLogs: many(heygenUsageLog)
  })
);

export const heygenUsageLogRelations = relations(heygenUsageLog, ({ one }) => ({
  organization: one(organizationTable, {
    fields: [heygenUsageLog.organizationId],
    references: [organizationTable.id]
  }),
  avatarGenerationJob: one(avatarGenerationJobs, {
    fields: [heygenUsageLog.avatarGenerationJobId],
    references: [avatarGenerationJobs.id]
  })
}));
// Step Images
export const stepImages = pgTable(
  'stepImages',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    projectId: uuid('projectId')
      .notNull()
      .references(() => projects.id, { onDelete: 'cascade' }),
    s3Url: text('s3Url').notNull(),
    timestampRange: jsonb('timestampRange').notNull(), // { start: number, end: number } in seconds
    createdAt: timestamp('createdAt', { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp('updatedAt', { withTimezone: true })
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date())
  },
  (table) => [
    index('IX_stepImages_projectId').using('btree', table.projectId.asc())
  ]
);

export const stepImagesRelations = relations(stepImages, ({ one }) => ({
  project: one(projects, {
    fields: [stepImages.projectId],
    references: [projects.id]
  })
}));
